import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  Platform,
  Modal,
  TextInput,
  ScrollView,
  Dimensions,
  PanResponder,
  GestureResponderEvent } from 'react-native'
import Svg, { Rect, Text as SvgText, G, Line, Circle, Path } from 'react-native-svg'
import { ArrowLeft, Plus, Minus, X, Clipboard, RotateCw, Trash2, CirclePlus, MousePointer2, Hash, Ruler } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { useAuth } from '../contexts/AuthContext'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import theme from '../theme'
import {
  Button,
  ButtonText,
  Card,
  Input,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'
const merchantColors = {
  primary: '#C07A5F',
  primaryLight: '#D4A088',
  accent: '#748C70' }

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'FloorPlanEditor'>
  route: RouteProp<RootStackParamList, 'FloorPlanEditor'>
}

interface Floor {
  id: string
  name: string
  width_cm: number
  height_cm: number
  background_image_url: string | null
  sort_order: number
}

interface SpotType {
  id: string
  name: string
  width_cm: number
  depth_cm: number
  color: string
  price_per_day: number | null
  price_per_week: number | null
  price_per_month: number | null
}

interface Spot {
  id: string
  floor_id: string
  spot_type_id: string
  spot_number: string
  x_position: number
  y_position: number
  rotation: number
  is_available: boolean
  is_active: boolean
  notes: string | null
  spot_type?: SpotType
}

// Floor element types for walls and amenities
type FloorElementType = 'wall' | 'door' | 'window' | 'pillar' | 'stairs' | 'elevator' | 'wc' | 'sink' | 'electrical_outlet' | 'fire_exit' | 'entrance' | 'counter' | 'storage' | 'other'

interface FloorElement {
  id: string
  floor_id: string
  element_type: FloorElementType
  // For walls: start and end points
  x1?: number
  y1?: number
  x2?: number
  y2?: number
  // For amenities: position
  x_position?: number
  y_position?: number
  width_cm: number
  height_cm: number
  rotation: number
  wall_thickness: number
  color: string
  label?: string
  notes?: string
  is_active: boolean
}

// Amenity presets with icons and default sizes (labels use translation keys matching type)
const AMENITY_PRESETS: { type: FloorElementType; icon: string; labelKey: string; width: number; height: number; color: string }[] = [
  { type: 'door', icon: '🚪', labelKey: 'door', width: 90, height: 10, color: '#8B4513' },
  { type: 'window', icon: '🪟', labelKey: 'window', width: 100, height: 10, color: '#87CEEB' },
  { type: 'pillar', icon: '⬛', labelKey: 'pillar', width: 40, height: 40, color: '#666666' },
  { type: 'stairs', icon: '🪜', labelKey: 'stairs', width: 120, height: 200, color: '#A0522D' },
  { type: 'elevator', icon: '🛗', labelKey: 'elevator', width: 150, height: 150, color: '#708090' },
  { type: 'wc', icon: '🚻', labelKey: 'wc', width: 200, height: 200, color: '#4682B4' },
  { type: 'sink', icon: '🚰', labelKey: 'sink', width: 60, height: 50, color: '#B0C4DE' },
  { type: 'electrical_outlet', icon: '🔌', labelKey: 'electrical_outlet', width: 20, height: 20, color: '#FFD700' },
  { type: 'fire_exit', icon: '🚨', labelKey: 'fire_exit', width: 100, height: 10, color: '#FF4500' },
  { type: 'entrance', icon: '🚶', labelKey: 'entrance', width: 120, height: 10, color: '#32CD32' },
  { type: 'counter', icon: '🛒', labelKey: 'counter', width: 200, height: 80, color: '#DEB887' },
  { type: 'storage', icon: '📦', labelKey: 'storage', width: 300, height: 200, color: '#D2691E' },
]

// Default floor dimensions (10m x 8m)
const DEFAULT_FLOOR_WIDTH = 1000
const DEFAULT_FLOOR_DEPTH = 800

// Grid snap (10cm)
const GRID_SIZE = 10

// Preset colors
const PRESET_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#6B7280', // Gray
  '#14B8A6', // Teal
]

// Spot type presets (nameKey is translation key)
const SPOT_TYPE_PRESETS = [
  { nameKey: 'table', width_cm: 120, depth_cm: 60 },
  { nameKey: 'large_table', width_cm: 180, depth_cm: 80 },
  { nameKey: 'rack', width_cm: 100, depth_cm: 50 },
  { nameKey: 'shelf', width_cm: 80, depth_cm: 40 },
  { nameKey: 'booth', width_cm: 200, depth_cm: 200 },
]

export default function FloorPlanEditorScreen({ navigation, route }: Props) {
  const { marketId } = route.params
  const { t } = useLanguage()
  const { user } = useAuth()

  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null)
  const [saving, setSaving] = useState(false)
  const [floors, setFloors] = useState<Floor[]>([])
  const [selectedFloor, setSelectedFloor] = useState<Floor | null>(null)
  const [spotTypes, setSpotTypes] = useState<SpotType[]>([])
  const [spots, setSpots] = useState<Spot[]>([])
  const [selectedSpot, setSelectedSpot] = useState<Spot | null>(null)
  const [selectedSpotType, setSelectedSpotType] = useState<SpotType | null>(null)

  // Zoom level (pixels per cm: 0.5 = small, 1 = normal, 2 = large)
  const [zoomLevel, setZoomLevel] = useState(0.5) // Start at 50% to fit larger floors
  const MIN_ZOOM = 0.1  // Very zoomed out (good for large floors)
  const MAX_ZOOM = 5    // Very zoomed in (good for detailed work)

  // Modals
  const [floorModalVisible, setFloorModalVisible] = useState(false)
  const [floorEditModalVisible, setFloorEditModalVisible] = useState(false)
  const [spotTypeModalVisible, setSpotTypeModalVisible] = useState(false)
  const [spotDetailModalVisible, setSpotDetailModalVisible] = useState(false)
  const [quickAddVisible, setQuickAddVisible] = useState(false)

  // Floor form (for create) - values in meters
  const [floorForm, setFloorForm] = useState({
    name: '',
    width_m: '10',
    depth_m: '8' })

  // Floor edit form - values in meters
  const [floorEditForm, setFloorEditForm] = useState({
    id: '',
    name: '',
    width_m: '',
    depth_m: '' })

  // Quick add spot type form
  const [quickAddForm, setQuickAddForm] = useState({
    name: 'Pöytä',
    width_cm: '120',
    depth_cm: '60',
    color: PRESET_COLORS[0] })

  // Canvas dimensions
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })

  // Dragging state
  const [draggingSpot, setDraggingSpot] = useState<Spot | null>(null)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [dragPosition, setDragPosition] = useState<{ x: number, y: number } | null>(null)

  // Multi-select state
  const [selectedSpots, setSelectedSpots] = useState<Set<string>>(new Set())
  const [selectionBox, setSelectionBox] = useState<{ startX: number, startY: number, endX: number, endY: number } | null>(null)
  const [isDraggingSelection, setIsDraggingSelection] = useState(false)
  const [bulkDragOffset, setBulkDragOffset] = useState({ x: 0, y: 0 })
  const [bulkDragDelta, setBulkDragDelta] = useState<{ x: number, y: number } | null>(null)

  // Copy/paste (single or bulk)
  const [copiedSpots, setCopiedSpots] = useState<Spot[]>([])

  // Floor elements (walls and amenities)
  const [floorElements, setFloorElements] = useState<FloorElement[]>([])
  const [selectedElement, setSelectedElement] = useState<FloorElement | null>(null)
  const [selectedAmenityType, setSelectedAmenityType] = useState<typeof AMENITY_PRESETS[0] | null>(null)

  // Wall drawing state
  const [wallDrawing, setWallDrawing] = useState<{ x1: number, y1: number } | null>(null)
  const [wallThickness, setWallThickness] = useState(10) // cm
  const [wallColor, setWallColor] = useState('#333333')

  // Wall endpoint dragging state
  const [draggingWallEndpoint, setDraggingWallEndpoint] = useState<{ elementId: string, endpoint: 'start' | 'end' } | null>(null)

  // Element dragging state (for moving entire walls/amenities)
  const [draggingElement, setDraggingElement] = useState<FloorElement | null>(null)
  const [elementDragOffset, setElementDragOffset] = useState({ x: 0, y: 0 })

  // Amenity resize state
  const [resizingAmenity, setResizingAmenity] = useState<{ elementId: string, corner: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' } | null>(null)
  const [resizeStartData, setResizeStartData] = useState<{ x: number, y: number, width: number, height: number, mouseX: number, mouseY: number } | null>(null)

  // Hover cursor state
  const [hoverCursor, setHoverCursor] = useState<string>('default')

  // Element detail modal
  const [elementDetailModalVisible, setElementDetailModalVisible] = useState(false)

  // Tool mode
  const [toolMode, setToolMode] = useState<'select' | 'add' | 'number' | 'wall' | 'amenity'>('select')
  const [numberingCounter, setNumberingCounter] = useState(1)
  const [numberingPrefix, setNumberingPrefix] = useState('')

  // Canvas scroll position
  const scrollViewRef = useRef<ScrollView>(null)
  const innerScrollViewRef = useRef<ScrollView>(null)

  // Middle mouse button panning state
  const [isPanning, setIsPanning] = useState(false)
  const panStartRef = useRef({ x: 0, y: 0, scrollX: 0, scrollY: 0 })

  // Throttle ref for mouse move events (to reduce re-renders)
  const lastMoveTimeRef = useRef<number>(0)
  const THROTTLE_MS = 16 // ~60fps

  useEffect(() => {
    fetchData()
  }, [marketId])

  // Refs for keyboard handler to access current state without re-running effect
  const selectedSpotsRef = useRef(selectedSpots)
  const selectedSpotRef = useRef(selectedSpot)
  const copiedSpotsRef = useRef(copiedSpots)
  const spotsRef = useRef(spots)
  const selectedElementRef = useRef(selectedElement)

  // Keep refs in sync
  useEffect(() => { selectedSpotsRef.current = selectedSpots }, [selectedSpots])
  useEffect(() => { selectedSpotRef.current = selectedSpot }, [selectedSpot])
  useEffect(() => { copiedSpotsRef.current = copiedSpots }, [copiedSpots])
  useEffect(() => { spotsRef.current = spots }, [spots])
  useEffect(() => { selectedElementRef.current = selectedElement }, [selectedElement])

  // Keyboard shortcuts for copy/paste (web only)
  useEffect(() => {
    if (Platform.OS !== 'web') return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+C or Cmd+C - Copy
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        if (selectedSpotsRef.current.size > 0 || selectedSpotRef.current) {
          e.preventDefault()
          // Copy selected spots
          if (selectedSpotsRef.current.size > 0) {
            const spotsToCopy = spotsRef.current.filter(s => selectedSpotsRef.current.has(s.id))
            setCopiedSpots(spotsToCopy)
          } else if (selectedSpotRef.current) {
            setCopiedSpots([selectedSpotRef.current])
          }
          setToolMode('add')
        }
      }
      // Ctrl+V or Cmd+V - Paste (switch to add mode)
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        if (copiedSpotsRef.current.length > 0) {
          e.preventDefault()
          setToolMode('add')
        }
      }
      // Delete or Backspace - Delete selected spots or elements
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Don't delete if typing in an input
        if ((e.target as HTMLElement)?.tagName === 'INPUT') return

        // Delete selected element (wall/amenity)
        if (selectedElementRef.current) {
          e.preventDefault()
          deleteFloorElementDirect(selectedElementRef.current.id)
          return
        }

        if (selectedSpotsRef.current.size > 0) {
          e.preventDefault()
          // Delete all selected spots
          selectedSpotsRef.current.forEach(spotId => deleteSpot(spotId))
          setSelectedSpots(new Set())
        } else if (selectedSpotRef.current) {
          e.preventDefault()
          deleteSpot(selectedSpotRef.current.id)
          setSelectedSpot(null)
        }
      }
      // Escape - Clear selection and copied spots
      if (e.key === 'Escape') {
        setSelectedSpot(null)
        setSelectedSpots(new Set())
        setCopiedSpots([])
        setToolMode('select')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, []) // Empty deps - effect only runs once

  const fetchData = async () => {
    setLoading(true)
    console.log('[FloorPlanEditor] Fetching data for market:', marketId)
    try {
      // Check if user is admin/owner of this market
      if (!user?.id) {
        setIsAdmin(false)
        setLoading(false)
        return
      }

      const { data: staffData } = await supabase
        .from('flea_market_staff')
        .select('role')
        .eq('flea_market_id', marketId)
        .eq('user_id', user.id)
        .is('deactivated_at', null)
        .single()

      const hasAccess = staffData?.role === 'admin' || staffData?.role === 'owner'
      setIsAdmin(hasAccess)

      if (!hasAccess) {
        setLoading(false)
        return
      }

      // Fetch floors
      const { data: floorsData, error: floorsError } = await supabase
        .from('floors')
        .select('*')
        .eq('flea_market_id', marketId)
        .order('sort_order')

      console.log('[FloorPlanEditor] Floors result:', { floors: floorsData?.length, error: floorsError })

      if (floorsError) throw floorsError
      setFloors(floorsData || [])

      if (floorsData && floorsData.length > 0) {
        setSelectedFloor(floorsData[0])
      }

      // Fetch spot types
      const { data: typesData, error: typesError } = await supabase
        .from('spot_types')
        .select('*')
        .eq('flea_market_id', marketId)
        .eq('is_active', true)
        .order('name')

      console.log('[FloorPlanEditor] Spot types result:', { types: typesData?.length, error: typesError })

      if (typesError) throw typesError
      setSpotTypes(typesData || [])

      if (typesData && typesData.length > 0) {
        setSelectedSpotType(typesData[0])
      }

    } catch (error) {
      console.error('[FloorPlanEditor] Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  // Fetch spots and floor elements when floor changes
  useEffect(() => {
    if (selectedFloor) {
      fetchSpots(selectedFloor.id)
      fetchFloorElements(selectedFloor.id)
    } else {
      setSpots([])
      setFloorElements([])
    }
  }, [selectedFloor])

  // Track which floor we've auto-fitted zoom for (to avoid resetting on re-renders)
  const autoFittedFloorRef = useRef<string | null>(null)

  // Auto-fit zoom only when floor changes (not on every container resize)
  useEffect(() => {
    if (selectedFloor && containerSize.width > 0 && containerSize.height > 0) {
      // Only auto-fit if this is a new floor we haven't fitted yet
      if (autoFittedFloorRef.current !== selectedFloor.id) {
        const padding = 40 // Small padding for rulers/edges
        const fitScaleX = (containerSize.width - padding) / selectedFloor.width_cm
        const fitScaleY = (containerSize.height - padding) / selectedFloor.height_cm
        const fitScale = Math.min(fitScaleX, fitScaleY)
        setZoomLevel(Math.max(fitScale, MIN_ZOOM))
        autoFittedFloorRef.current = selectedFloor.id
      }
    }
  }, [selectedFloor?.id, containerSize.width, containerSize.height])

  const fetchSpots = async (floorId: string) => {
    try {
      const { data, error } = await supabase
        .from('spots')
        .select('*, spot_type:spot_types(*)')
        .eq('floor_id', floorId)
        .is('deleted_at', null)
        .order('spot_number')

      if (error) throw error
      setSpots(data || [])
    } catch (error) {
      console.error('Error fetching spots:', error)
    }
  }

  const fetchFloorElements = async (floorId: string) => {
    try {
      const { data, error } = await supabase
        .from('floor_elements')
        .select('*')
        .eq('floor_id', floorId)
        .is('deleted_at', null)
        .eq('is_active', true)

      if (error) throw error
      setFloorElements(data || [])
    } catch (error) {
      console.error('Error fetching floor elements:', error)
    }
  }

  // Create a wall element
  const createWall = async (x1: number, y1: number, x2: number, y2: number) => {
    if (!selectedFloor) return

    try {
      const { data, error } = await supabase
        .from('floor_elements')
        .insert({
          floor_id: selectedFloor.id,
          element_type: 'wall',
          x1: Math.round(x1),
          y1: Math.round(y1),
          x2: Math.round(x2),
          y2: Math.round(y2),
          wall_thickness: wallThickness,
          color: wallColor,
          is_active: true })
        .select()
        .single()

      if (error) throw error
      if (data) {
        setFloorElements([...floorElements, data])
        // Switch to select mode and select the newly created wall
        setToolMode('select')
        setSelectedElement(data)
        setSelectedSpot(null)
      }
    } catch (error: any) {
      console.error('Error creating wall:', error)
      showAlert(t('error'), error.message || 'Failed to create wall')
    }
  }

  // Create an amenity element
  const createAmenity = async (x: number, y: number, amenityType: typeof AMENITY_PRESETS[0]) => {
    if (!selectedFloor) return

    try {
      const { data, error } = await supabase
        .from('floor_elements')
        .insert({
          floor_id: selectedFloor.id,
          element_type: amenityType.type,
          x_position: Math.round(x),
          y_position: Math.round(y),
          width_cm: amenityType.width,
          height_cm: amenityType.height,
          color: amenityType.color,
          label: t(amenityType.labelKey),
          rotation: 0,
          is_active: true })
        .select()
        .single()

      if (error) throw error
      if (data) {
        setFloorElements([...floorElements, data])
      }
    } catch (error: any) {
      console.error('Error creating amenity:', error)
      showAlert(t('error'), error.message || 'Failed to create amenity')
    }
  }

  // Update floor element position
  const updateElementPosition = async (elementId: string, updates: Partial<FloorElement>) => {
    try {
      const { error } = await supabase
        .from('floor_elements')
        .update(updates)
        .eq('id', elementId)

      if (error) throw error

      setFloorElements(floorElements.map(el =>
        el.id === elementId ? { ...el, ...updates } : el
      ))
    } catch (error) {
      console.error('Error updating element:', error)
    }
  }

  // Delete floor element directly (no confirmation)
  const deleteFloorElementDirect = async (elementId: string) => {
    try {
      const { error } = await supabase
        .from('floor_elements')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', elementId)

      if (error) throw error

      setFloorElements(prev => prev.filter(el => el.id !== elementId))
      setSelectedElement(null)
      setElementDetailModalVisible(false)
    } catch (error: any) {
      console.error('Error deleting element:', error)
    }
  }

  // Delete floor element with confirmation
  const deleteFloorElement = async (elementId: string) => {
    confirmAction(
      t('delete_element') || 'Delete Element',
      t('confirm_delete_element') || 'Are you sure you want to delete this element?',
      () => deleteFloorElementDirect(elementId)
    )
  }

  // Rotate floor element
  const rotateElement = async (elementId: string) => {
    const element = floorElements.find(el => el.id === elementId)
    if (!element) return

    const newRotation = (element.rotation + 90) % 360

    setFloorElements(floorElements.map(el =>
      el.id === elementId ? { ...el, rotation: newRotation } : el
    ))

    try {
      const { error } = await supabase
        .from('floor_elements')
        .update({ rotation: newRotation })
        .eq('id', elementId)

      if (error) throw error
    } catch (error) {
      console.error('Error rotating element:', error)
    }
  }

  const showAlert = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      window.alert(`${title}\n\n${message}`)
    } else {
      Alert.alert(title, message)
    }
  }

  const confirmAction = (title: string, message: string, onConfirm: () => void) => {
    if (Platform.OS === 'web') {
      if (window.confirm(`${title}\n\n${message}`)) {
        onConfirm()
      }
    } else {
      Alert.alert(title, message, [
        { text: t('cancel') || 'Cancel', style: 'cancel' },
        { text: t('confirm') || 'Confirm', onPress: onConfirm },
      ])
    }
  }

  // Base scale: pixels per cm (1 pixel = 1cm at 100% zoom)
  // This gives a reasonable size for typical floor plans
  const BASE_PIXELS_PER_CM = 1

  // Get scale based on zoom
  const getScale = () => {
    return BASE_PIXELS_PER_CM * zoomLevel
  }

  // Zoom controls
  const zoomIn = () => {
    setZoomLevel(prev => Math.min(prev * 1.25, MAX_ZOOM))
  }

  const zoomOut = () => {
    setZoomLevel(prev => Math.max(prev / 1.25, MIN_ZOOM))
  }

  const resetZoom = () => {
    // Fit to container with minimal padding
    if (selectedFloor && containerSize.width > 0 && containerSize.height > 0) {
      const padding = 40 // Small padding for rulers/edges
      const fitScaleX = (containerSize.width - padding) / selectedFloor.width_cm
      const fitScaleY = (containerSize.height - padding) / selectedFloor.height_cm
      const fitScale = Math.min(fitScaleX, fitScaleY)
      setZoomLevel(Math.max(fitScale, MIN_ZOOM))
    } else {
      setZoomLevel(0.5)
    }
  }

  // Open floor edit modal
  const openFloorEditModal = (floor: Floor) => {
    setFloorEditForm({
      id: floor.id,
      name: floor.name,
      width_m: String(floor.width_cm / 100), // Convert cm to m
      depth_m: String(floor.height_cm / 100), // Convert cm to m, DB uses height_cm
    })
    setFloorEditModalVisible(true)
  }

  // Update floor
  const updateFloor = async () => {
    if (!floorEditForm.name.trim()) {
      showAlert(t('error'), t('enter_floor_name') || 'Please enter a floor name')
      return
    }

    const widthCm = Math.round((parseFloat(floorEditForm.width_m) || 10) * 100)
    const depthCm = Math.round((parseFloat(floorEditForm.depth_m) || 8) * 100)

    setSaving(true)
    try {
      const { data, error } = await supabase
        .from('floors')
        .update({
          name: floorEditForm.name.trim(),
          width_cm: widthCm || DEFAULT_FLOOR_WIDTH,
          height_cm: depthCm || DEFAULT_FLOOR_DEPTH, // DB uses height_cm
        })
        .eq('id', floorEditForm.id)
        .select()
        .single()

      if (error) throw error

      // Update local state
      setFloors(floors.map(f => f.id === data.id ? data : f))
      if (selectedFloor?.id === data.id) {
        setSelectedFloor(data)
      }
      setFloorEditModalVisible(false)
    } catch (error: any) {
      console.error('Error updating floor:', error)
      showAlert(t('error'), error.message || 'Failed to update floor')
    } finally {
      setSaving(false)
    }
  }

  // Delete floor
  const deleteFloor = async (floorId: string) => {
    const floorToDelete = floors.find(f => f.id === floorId)
    if (!floorToDelete) return

    confirmAction(
      t('delete_floor') || 'Delete Floor',
      t('confirm_delete_floor') || `Are you sure you want to delete "${floorToDelete.name}"? All spots on this floor will also be deleted.`,
      async () => {
        setSaving(true)
        try {
          // First delete all spots on this floor
          const { error: spotsError } = await supabase
            .from('spots')
            .update({ deleted_at: new Date().toISOString() })
            .eq('floor_id', floorId)

          if (spotsError) throw spotsError

          // Then delete the floor
          const { error } = await supabase
            .from('floors')
            .delete()
            .eq('id', floorId)

          if (error) throw error

          // Update local state
          const newFloors = floors.filter(f => f.id !== floorId)
          setFloors(newFloors)

          // If the deleted floor was selected, select another floor
          if (selectedFloor?.id === floorId) {
            setSelectedFloor(newFloors.length > 0 ? newFloors[0] : null)
          }

          setFloorEditModalVisible(false)
        } catch (error: any) {
          console.error('Error deleting floor:', error)
          showAlert(t('error'), error.message || 'Failed to delete floor')
        } finally {
          setSaving(false)
        }
      }
    )
  }

  // Create floor
  const createFloor = async () => {
    if (!floorForm.name.trim()) {
      showAlert(t('error'), t('enter_floor_name') || 'Please enter a floor name')
      return
    }

    const widthCm = Math.round((parseFloat(floorForm.width_m) || 10) * 100)
    const depthCm = Math.round((parseFloat(floorForm.depth_m) || 8) * 100)

    console.log('[FloorPlanEditor] Creating floor:', { marketId, name: floorForm.name.trim(), widthCm, depthCm })

    setSaving(true)
    try {
      const { data, error } = await supabase
        .from('floors')
        .insert({
          flea_market_id: marketId,
          name: floorForm.name.trim(),
          width_cm: widthCm || DEFAULT_FLOOR_WIDTH,
          height_cm: depthCm || DEFAULT_FLOOR_DEPTH, // DB uses height_cm
          sort_order: floors.length })
        .select()
        .single()

      console.log('[FloorPlanEditor] Floor created:', { data, error })

      if (error) throw error

      setFloors([...floors, data])
      setSelectedFloor(data)
      setFloorModalVisible(false)
      setFloorForm({ name: '', width_m: '10', depth_m: '8' })
    } catch (error: any) {
      console.error('[FloorPlanEditor] Error creating floor:', error)
      showAlert(t('error'), error.message || 'Failed to create floor')
    } finally {
      setSaving(false)
    }
  }

  // Quick add spot type
  const quickAddSpotType = async () => {
    if (!quickAddForm.name.trim()) {
      showAlert(t('error'), t('enter_name') || 'Please enter a name')
      return
    }

    setSaving(true)
    try {
      const { data, error } = await supabase
        .from('spot_types')
        .insert({
          flea_market_id: marketId,
          name: quickAddForm.name.trim(),
          width_cm: parseInt(quickAddForm.width_cm) || 100,
          depth_cm: parseInt(quickAddForm.depth_cm) || 60,
          color: quickAddForm.color,
          price_per_day: 10, // Default price
        })
        .select()
        .single()

      if (error) throw error

      setSpotTypes([...spotTypes, data])
      setSelectedSpotType(data)
      setQuickAddVisible(false)
      setToolMode('add')
    } catch (error: any) {
      console.error('Error creating spot type:', error)
      showAlert(t('error'), error.message || 'Failed to create type')
    } finally {
      setSaving(false)
    }
  }

  // Check overlap for new spot (uses 'new' as spotId to skip self-check)
  const checkNewSpotOverlap = (x: number, y: number, width: number, height: number): boolean => {
    for (const other of spots) {
      const otherType = other.spot_type || spotTypes.find(t => t.id === other.spot_type_id)
      if (!otherType) continue

      const otherRotated = other.rotation === 90 || other.rotation === 270
      const otherWidth = otherRotated ? otherType.depth_cm : otherType.width_cm
      const otherHeight = otherRotated ? otherType.width_cm : otherType.depth_cm

      // Check rectangle intersection
      const noOverlap =
        x + width <= other.x_position ||
        other.x_position + otherWidth <= x ||
        y + height <= other.y_position ||
        other.y_position + otherHeight <= y

      if (!noOverlap) return true
    }
    return false
  }

  // Add spot at position
  const addSpotAtPosition = async (x: number, y: number, spotTypeOverride?: SpotType) => {
    const typeToUse = spotTypeOverride || selectedSpotType
    if (!selectedFloor || !typeToUse) {
      showAlert(t('error'), t('select_floor_and_type') || 'Please select a floor and create a table type first')
      return
    }

    // Get dimensions
    const rotation = copiedSpots[0]?.rotation || 0
    const rotated = rotation === 90 || rotation === 270
    const spotWidth = rotated ? typeToUse.depth_cm : typeToUse.width_cm
    const spotHeight = rotated ? typeToUse.width_cm : typeToUse.depth_cm

    // Find nearest available position
    const position = findNearestAvailablePosition(x, y, spotWidth, spotHeight)
    if (!position) {
      showAlert(t('error'), t('no_space') || 'No available space on the floor')
      return
    }

    // Generate next spot number - query DB to ensure uniqueness
    const prefix = typeToUse.name.charAt(0).toUpperCase()
    const { data: existingSpots } = await supabase
      .from('spots')
      .select('spot_number')
      .eq('floor_id', selectedFloor.id)
      .like('spot_number', `${prefix}%`)

    const existingNumbers = new Set((existingSpots || []).map(s => s.spot_number))
    let nextNumber = 1
    while (existingNumbers.has(`${prefix}${nextNumber}`)) {
      nextNumber++
    }
    const spotNumber = `${prefix}${nextNumber}`

    setSaving(true)
    try {
      const { data, error } = await supabase
        .from('spots')
        .insert({
          floor_id: selectedFloor.id,
          spot_type_id: typeToUse.id,
          spot_number: spotNumber,
          x_position: position.x,
          y_position: position.y,
          rotation: rotation,
          is_available: true,
          is_active: true })
        .select('*, spot_type:spot_types(*)')
        .single()

      if (error) throw error

      setSpots([...spots, data])
    } catch (error: any) {
      console.error('Error adding spot:', error)
      showAlert(t('error'), error.message || 'Failed to add spot')
    } finally {
      setSaving(false)
    }
  }

  // Update spot position
  const updateSpotPosition = async (spotId: string, x: number, y: number) => {
    // Snap to grid
    const snappedX = Math.round(x / GRID_SIZE) * GRID_SIZE
    const snappedY = Math.round(y / GRID_SIZE) * GRID_SIZE

    // Clamp to floor bounds
    const floor = selectedFloor
    if (!floor) return

    const spot = spots.find(s => s.id === spotId)
    const spotType = spot?.spot_type || spotTypes.find(t => t.id === spot?.spot_type_id)
    if (!spotType) return

    const rotated = spot?.rotation === 90 || spot?.rotation === 270
    const spotWidth = rotated ? spotType.depth_cm : spotType.width_cm
    const spotHeight = rotated ? spotType.width_cm : spotType.depth_cm

    const clampedX = Math.max(0, Math.min(snappedX, floor.width_cm - spotWidth))
    const clampedY = Math.max(0, Math.min(snappedY, floor.height_cm - spotHeight))

    // Update locally first for responsiveness
    setSpots(spots.map(s =>
      s.id === spotId ? { ...s, x_position: clampedX, y_position: clampedY } : s
    ))

    try {
      const { error } = await supabase
        .from('spots')
        .update({ x_position: clampedX, y_position: clampedY })
        .eq('id', spotId)

      if (error) throw error
    } catch (error) {
      console.error('Error updating spot position:', error)
      // Refetch on error
      if (selectedFloor) fetchSpots(selectedFloor.id)
    }
  }

  // Find nearest available position (spiral search from target position)
  const findNearestAvailablePosition = (
    targetX: number,
    targetY: number,
    width: number,
    height: number,
    excludeSpotId?: string
  ): { x: number, y: number } | null => {
    if (!selectedFloor) return null

    // Snap target to grid
    const startX = Math.round(targetX / GRID_SIZE) * GRID_SIZE
    const startY = Math.round(targetY / GRID_SIZE) * GRID_SIZE

    // Check if target position is available
    const checkPosition = (x: number, y: number): boolean => {
      // Check bounds
      if (x < 0 || y < 0 || x + width > selectedFloor.width_cm || y + height > selectedFloor.height_cm) {
        return false
      }
      // Check overlap
      for (const other of spots) {
        if (excludeSpotId && other.id === excludeSpotId) continue
        const otherType = other.spot_type || spotTypes.find(t => t.id === other.spot_type_id)
        if (!otherType) continue
        const otherRotated = other.rotation === 90 || other.rotation === 270
        const otherWidth = otherRotated ? otherType.depth_cm : otherType.width_cm
        const otherHeight = otherRotated ? otherType.width_cm : otherType.depth_cm
        const noOverlap =
          x + width <= other.x_position ||
          other.x_position + otherWidth <= x ||
          y + height <= other.y_position ||
          other.y_position + otherHeight <= y
        if (!noOverlap) return false
      }
      return true
    }

    // If target is available, use it
    if (checkPosition(startX, startY)) {
      return { x: startX, y: startY }
    }

    // Spiral search outward
    const maxDistance = Math.max(selectedFloor.width_cm, selectedFloor.height_cm)
    for (let distance = GRID_SIZE; distance < maxDistance; distance += GRID_SIZE) {
      // Check positions in a square ring around the target
      for (let dx = -distance; dx <= distance; dx += GRID_SIZE) {
        for (let dy = -distance; dy <= distance; dy += GRID_SIZE) {
          // Only check the perimeter of this ring
          if (Math.abs(dx) !== distance && Math.abs(dy) !== distance) continue
          const x = startX + dx
          const y = startY + dy
          if (checkPosition(x, y)) {
            return { x, y }
          }
        }
      }
    }

    return null // No position found
  }

  // Copy spot(s) - works with single selection or multi-selection
  const copySpot = (spot?: Spot) => {
    if (selectedSpots.size > 0) {
      // Copy all selected spots
      const spotsToCopy = spots.filter(s => selectedSpots.has(s.id))
      setCopiedSpots(spotsToCopy)
    } else if (spot) {
      // Copy single spot
      setCopiedSpots([spot])
    }
    setToolMode('add')
  }

  // Paste copied spots at position with auto-incrementing numbers
  const pasteSpots = async (baseX: number, baseY: number) => {
    if (!selectedFloor || copiedSpots.length === 0) return

    setSaving(true)
    try {
      // Calculate offset from the first copied spot's position
      const firstSpot = copiedSpots[0]
      const offsetX = baseX - firstSpot.x_position
      const offsetY = baseY - firstSpot.y_position

      // Get all existing spot numbers for this floor
      const { data: existingSpots } = await supabase
        .from('spots')
        .select('spot_number')
        .eq('floor_id', selectedFloor.id)

      const existingNumbers = new Set((existingSpots || []).map(s => s.spot_number))

      // Track numbers we're about to add to avoid duplicates within the paste batch
      const newNumbers = new Set<string>()

      const spotsToInsert = []

      for (const spot of copiedSpots) {
        const spotType = spot.spot_type || spotTypes.find(t => t.id === spot.spot_type_id)
        if (!spotType) continue

        // Calculate new position with offset
        const newX = Math.round((spot.x_position + offsetX) / GRID_SIZE) * GRID_SIZE
        const newY = Math.round((spot.y_position + offsetY) / GRID_SIZE) * GRID_SIZE

        // Clamp to floor bounds
        const rotated = spot.rotation === 90 || spot.rotation === 270
        const spotWidth = rotated ? spotType.depth_cm : spotType.width_cm
        const spotHeight = rotated ? spotType.width_cm : spotType.depth_cm
        const clampedX = Math.max(0, Math.min(newX, selectedFloor.width_cm - spotWidth))
        const clampedY = Math.max(0, Math.min(newY, selectedFloor.height_cm - spotHeight))

        // Generate unique number - extract prefix and find next available
        const match = spot.spot_number.match(/^([A-Za-z]*)(\d+)$/)
        const prefix = match ? match[1] : spotType.name.charAt(0).toUpperCase()
        let nextNumber = match ? parseInt(match[2]) + 1 : 1
        let spotNumber = `${prefix}${nextNumber}`

        while (existingNumbers.has(spotNumber) || newNumbers.has(spotNumber)) {
          nextNumber++
          spotNumber = `${prefix}${nextNumber}`
        }
        newNumbers.add(spotNumber)

        spotsToInsert.push({
          floor_id: selectedFloor.id,
          spot_type_id: spot.spot_type_id,
          spot_number: spotNumber,
          x_position: clampedX,
          y_position: clampedY,
          rotation: spot.rotation,
          is_available: true,
          is_active: true })
      }

      if (spotsToInsert.length > 0) {
        const { data, error } = await supabase
          .from('spots')
          .insert(spotsToInsert)
          .select('*, spot_type:spot_types(*)')

        if (error) throw error
        if (data) {
          setSpots([...spots, ...data])
        }
      }
    } catch (error: any) {
      console.error('Error pasting spots:', error)
      showAlert(t('error'), error.message || 'Failed to paste spots')
    } finally {
      setSaving(false)
    }
  }

  // Update spot number (for numbering mode) - enforces the requested number by orphaning duplicates
  const updateSpotNumber = async (spotId: string, requestedNumber: string) => {
    if (!selectedFloor) return

    try {
      // Check if number already exists on another spot
      const { data: conflictingSpots } = await supabase
        .from('spots')
        .select('id, spot_number')
        .eq('floor_id', selectedFloor.id)
        .eq('spot_number', requestedNumber)
        .neq('id', spotId)

      // If there's a conflict, first clear the number from the old spot (make it orphan)
      if (conflictingSpots && conflictingSpots.length > 0) {
        const conflictingSpot = conflictingSpots[0]
        // Generate a temporary orphan number
        const orphanNumber = `_${conflictingSpot.id.slice(0, 6)}`

        // Update the conflicting spot to orphan number in DB first
        await supabase
          .from('spots')
          .update({ spot_number: orphanNumber })
          .eq('id', conflictingSpot.id)

        // Update local state for orphaned spot
        setSpots(prev => prev.map(s =>
          s.id === conflictingSpot.id ? { ...s, spot_number: orphanNumber } : s
        ))
      }

      // Now update the target spot with the requested number
      const { error } = await supabase
        .from('spots')
        .update({ spot_number: requestedNumber })
        .eq('id', spotId)

      if (error) throw error

      // Update local state for the target spot
      setSpots(prev => prev.map(s =>
        s.id === spotId ? { ...s, spot_number: requestedNumber } : s
      ))
    } catch (error) {
      console.error('Error updating spot number:', error)
    }
  }

  // Delete spot
  const deleteSpot = async (spotId: string) => {
    confirmAction(
      t('delete_spot') || 'Delete Spot',
      t('confirm_delete_spot') || 'Are you sure you want to delete this spot?',
      async () => {
        try {
          const { error } = await supabase
            .from('spots')
            .update({ deleted_at: new Date().toISOString() })
            .eq('id', spotId)

          if (error) throw error

          setSpots(spots.filter(s => s.id !== spotId))
          setSelectedSpot(null)
          setSpotDetailModalVisible(false)
        } catch (error: any) {
          console.error('Error deleting spot:', error)
          showAlert(t('error'), error.message || 'Failed to delete spot')
        }
      }
    )
  }

  // Rotate spot
  const rotateSpot = async (spotId: string) => {
    const spot = spots.find(s => s.id === spotId)
    if (!spot) return

    const newRotation = (spot.rotation + 90) % 360

    // Update locally
    setSpots(spots.map(s =>
      s.id === spotId ? { ...s, rotation: newRotation } : s
    ))

    try {
      const { error } = await supabase
        .from('spots')
        .update({ rotation: newRotation })
        .eq('id', spotId)

      if (error) throw error
    } catch (error) {
      console.error('Error rotating spot:', error)
    }
  }

  // Track if we dragged (to prevent click after drag)
  const didDragRef = useRef(false)

  // Handle double-click to start dragging a spot
  const handleCanvasDoubleClick = (event: any) => {
    if (!selectedFloor || toolMode !== 'select') return

    const coords = getFloorCoordsFromEvent(event)
    if (!coords) return

    const clickedSpot = findSpotAtPosition(coords.x, coords.y)
    if (clickedSpot) {
      // Start dragging this spot
      setDraggingSpot(clickedSpot)
      setDragOffset({
        x: coords.x - clickedSpot.x_position,
        y: coords.y - clickedSpot.y_position })
      setDragPosition({ x: clickedSpot.x_position, y: clickedSpot.y_position })
      setSelectedSpot(clickedSpot)
      event.preventDefault?.()
    }
  }

  // Handle canvas touch/click
  const handleCanvasPress = (event: any) => {
    // Don't handle click if we just finished dragging
    if (didDragRef.current) {
      didDragRef.current = false
      return
    }

    if (!selectedFloor) return

    const coords = getFloorCoordsFromEvent(event)
    if (!coords) return

    const { x: floorX, y: floorY } = coords

    // In select mode, check if we clicked on a spot or element first
    if (toolMode === 'select') {
      const isCtrlHeld = event.nativeEvent?.ctrlKey || event.nativeEvent?.metaKey
      const clickedSpot = findSpotAtPosition(floorX, floorY)
      if (clickedSpot) {
        setSelectedSpot(clickedSpot)
        setSelectedElement(null)
        // No modal on click - just select
        return
      }

      // Check for floor elements (walls, amenities)
      const clickedElement = findElementAtPosition(floorX, floorY)
      if (clickedElement) {
        setSelectedElement(clickedElement)
        setSelectedSpot(null)
        // Don't show detail modal for walls or amenities - just select them
        if (false) {
          setElementDetailModalVisible(true)
        }
        return
      }

      // Clicked on empty space - deselect
      setSelectedSpot(null)
      setSelectedSpots(new Set())
      setSelectedElement(null)
      return
    }

    if (toolMode === 'add') {
      if (copiedSpots.length > 0) {
        // Paste copied spot(s)
        pasteSpots(floorX, floorY)
      } else if (selectedSpotType) {
        addSpotAtPosition(floorX, floorY)
      } else {
        // No spot type selected, show quick add
        setQuickAddVisible(true)
      }
    } else if (toolMode === 'number') {
      // Find spot at this position and assign number
      const clickedSpot = findSpotAtPosition(floorX, floorY)
      if (clickedSpot) {
        const newNumber = `${numberingPrefix}${numberingCounter}`
        updateSpotNumber(clickedSpot.id, newNumber)
        setNumberingCounter(prev => prev + 1)
      }
    } else if (toolMode === 'wall') {
      // Wall drawing - two click process
      const snappedX = Math.round(floorX / GRID_SIZE) * GRID_SIZE
      const snappedY = Math.round(floorY / GRID_SIZE) * GRID_SIZE

      if (!wallDrawing) {
        // First click - set start point
        setWallDrawing({ x1: snappedX, y1: snappedY })
      } else {
        // Second click - create wall
        createWall(wallDrawing.x1, wallDrawing.y1, snappedX, snappedY)
        setWallDrawing(null)
      }
    } else if (toolMode === 'amenity') {
      // Place amenity at click position
      if (selectedAmenityType) {
        const snappedX = Math.round(floorX / GRID_SIZE) * GRID_SIZE
        const snappedY = Math.round(floorY / GRID_SIZE) * GRID_SIZE
        createAmenity(snappedX, snappedY, selectedAmenityType)
      }
    }
  }

  // Find spot at position (searches in reverse order so top spots are found first)
  const findSpotAtPosition = (x: number, y: number): Spot | null => {
    for (let i = spots.length - 1; i >= 0; i--) {
      const spot = spots[i]
      const spotType = spot.spot_type || spotTypes.find(t => t.id === spot.spot_type_id)
      if (!spotType) continue

      const rotated = spot.rotation === 90 || spot.rotation === 270
      const width = rotated ? spotType.depth_cm : spotType.width_cm
      const height = rotated ? spotType.width_cm : spotType.depth_cm

      if (x >= spot.x_position && x <= spot.x_position + width &&
          y >= spot.y_position && y <= spot.y_position + height) {
        return spot
      }
    }
    return null
  }

  // Find floor element at position
  const findElementAtPosition = (x: number, y: number): FloorElement | null => {
    for (let i = floorElements.length - 1; i >= 0; i--) {
      const element = floorElements[i]

      if (element.element_type === 'wall') {
        // For walls, check if point is near the line
        if (element.x1 !== undefined && element.y1 !== undefined &&
            element.x2 !== undefined && element.y2 !== undefined) {
          const thickness = element.wall_thickness || 10
          const dist = pointToLineDistance(x, y, element.x1, element.y1, element.x2, element.y2)
          if (dist <= thickness / 2 + 10) { // 10cm tolerance
            return element
          }
        }
      } else {
        // For amenities, check bounding box
        if (element.x_position !== undefined && element.y_position !== undefined) {
          const rotated = element.rotation === 90 || element.rotation === 270
          const width = rotated ? element.height_cm : element.width_cm
          const height = rotated ? element.width_cm : element.height_cm

          if (x >= element.x_position && x <= element.x_position + width &&
              y >= element.y_position && y <= element.y_position + height) {
            return element
          }
        }
      }
    }
    return null
  }

  // Find amenity resize handle at position
  const findAmenityResizeHandle = (x: number, y: number): { element: FloorElement, corner: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' } | null => {
    const hitRadius = 20 // cm
    // Only check selected amenity
    if (!selectedElement || selectedElement.element_type === 'wall') return null
    if (selectedElement.x_position === undefined || selectedElement.y_position === undefined) return null

    const rotated = selectedElement.rotation === 90 || selectedElement.rotation === 270
    const width = rotated ? selectedElement.height_cm : selectedElement.width_cm
    const height = rotated ? selectedElement.width_cm : selectedElement.height_cm
    const ex = selectedElement.x_position
    const ey = selectedElement.y_position

    // Check corners
    const corners: { corner: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight', cx: number, cy: number }[] = [
      { corner: 'topLeft', cx: ex, cy: ey },
      { corner: 'topRight', cx: ex + width, cy: ey },
      { corner: 'bottomLeft', cx: ex, cy: ey + height },
      { corner: 'bottomRight', cx: ex + width, cy: ey + height },
    ]

    for (const { corner, cx, cy } of corners) {
      const dist = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2)
      if (dist <= hitRadius) {
        return { element: selectedElement, corner }
      }
    }
    return null
  }

  // Find wall endpoint at position (for dragging wall ends)
  const findWallEndpointAtPosition = (x: number, y: number): { element: FloorElement, endpoint: 'start' | 'end' } | null => {
    const hitRadius = 30 // cm - how close you need to click to grab an endpoint
    for (let i = floorElements.length - 1; i >= 0; i--) {
      const element = floorElements[i]
      if (element.element_type !== 'wall') continue
      if (element.x1 === undefined || element.y1 === undefined ||
          element.x2 === undefined || element.y2 === undefined) continue

      // Check start point
      const distStart = Math.sqrt((x - element.x1) ** 2 + (y - element.y1) ** 2)
      if (distStart <= hitRadius) {
        return { element, endpoint: 'start' }
      }

      // Check end point
      const distEnd = Math.sqrt((x - element.x2) ** 2 + (y - element.y2) ** 2)
      if (distEnd <= hitRadius) {
        return { element, endpoint: 'end' }
      }
    }
    return null
  }

  // Calculate distance from point to line segment
  const pointToLineDistance = (px: number, py: number, x1: number, y1: number, x2: number, y2: number): number => {
    const dx = x2 - x1
    const dy = y2 - y1
    const lengthSq = dx * dx + dy * dy

    if (lengthSq === 0) {
      // Line is a point
      return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2)
    }

    // Project point onto line
    let t = ((px - x1) * dx + (py - y1) * dy) / lengthSq
    t = Math.max(0, Math.min(1, t))

    const nearestX = x1 + t * dx
    const nearestY = y1 + t * dy

    return Math.sqrt((px - nearestX) ** 2 + (py - nearestY) ** 2)
  }

  // Check if a spot overlaps with any other spots
  const checkOverlap = (
    spotId: string,
    x: number,
    y: number,
    width: number,
    height: number
  ): boolean => {
    for (const other of spots) {
      if (other.id === spotId) continue // Skip self

      const otherType = other.spot_type || spotTypes.find(t => t.id === other.spot_type_id)
      if (!otherType) continue

      const otherRotated = other.rotation === 90 || other.rotation === 270
      const otherWidth = otherRotated ? otherType.depth_cm : otherType.width_cm
      const otherHeight = otherRotated ? otherType.width_cm : otherType.depth_cm

      // Check rectangle intersection
      const noOverlap =
        x + width <= other.x_position ||
        other.x_position + otherWidth <= x ||
        y + height <= other.y_position ||
        other.y_position + otherHeight <= y

      if (!noOverlap) return true
    }
    return false
  }

  // Get floor coordinates from event
  const getFloorCoordsFromEvent = (event: any): { x: number, y: number } | null => {
    if (!selectedFloor) return null
    const scale = getScale()

    if (Platform.OS === 'web') {
      const nativeEvent = event.nativeEvent
      const offsetX = nativeEvent.offsetX ?? nativeEvent.layerX ?? 0
      const offsetY = nativeEvent.offsetY ?? nativeEvent.layerY ?? 0
      return { x: offsetX / scale, y: offsetY / scale }
    } else {
      const { locationX, locationY } = event.nativeEvent
      return { x: locationX / scale, y: locationY / scale }
    }
  }

  // Handle mouse/touch down on canvas for drag start
  const handleCanvasMouseDown = (event: any) => {
    if (!selectedFloor) return

    // Get mouse button (0 = left, 1 = middle, 2 = right)
    const button = event.nativeEvent?.button ?? 0
    const isMiddleClick = button === 1
    const isRightClick = button === 2

    // Middle mouse button = pan viewport
    if (isMiddleClick && Platform.OS === 'web') {
      event.preventDefault?.()
      setIsPanning(true)

      // Get current scroll positions from native DOM elements
      const horizontalScroll = (scrollViewRef.current as any)?.getScrollableNode?.() ||
        (scrollViewRef.current as any)?._nativeRef?.current
      const verticalScroll = (innerScrollViewRef.current as any)?.getScrollableNode?.() ||
        (innerScrollViewRef.current as any)?._nativeRef?.current

      panStartRef.current = {
        x: event.nativeEvent?.clientX ?? 0,
        y: event.nativeEvent?.clientY ?? 0,
        scrollX: horizontalScroll?.scrollLeft ?? 0,
        scrollY: verticalScroll?.scrollTop ?? 0 }
      return
    }

    const coords = getFloorCoordsFromEvent(event)
    if (!coords) return

    const clickedSpot = findSpotAtPosition(coords.x, coords.y)

    // Right-click = drag/move spots or wall endpoints
    if (isRightClick && toolMode === 'select') {
      event.preventDefault?.()

      // Check if right-clicking on a wall endpoint to drag it
      const wallEndpoint = findWallEndpointAtPosition(coords.x, coords.y)
      if (wallEndpoint) {
        setDraggingWallEndpoint({ elementId: wallEndpoint.element.id, endpoint: wallEndpoint.endpoint })
        setSelectedElement(wallEndpoint.element)
        setSelectedSpot(null)
        return
      }

      // Check if right-clicking on a selected spot to start bulk drag
      if (clickedSpot && selectedSpots.has(clickedSpot.id) && selectedSpots.size > 1) {
        setIsDraggingSelection(true)
        setBulkDragOffset({ x: coords.x, y: coords.y })
        setBulkDragDelta({ x: 0, y: 0 })
        return
      }

      if (clickedSpot) {
        // Start dragging single spot
        setDraggingSpot(clickedSpot)
        setDragOffset({
          x: coords.x - clickedSpot.x_position,
          y: coords.y - clickedSpot.y_position })
        setDragPosition({ x: clickedSpot.x_position, y: clickedSpot.y_position })
        setSelectedSpot(clickedSpot)
      }
      return
    }

    // Left-click = select / selection box / wall endpoint drag / element drag / amenity resize
    if (!isRightClick && !isMiddleClick && toolMode === 'select') {
      // Check if clicking on an amenity resize handle
      const resizeHandle = findAmenityResizeHandle(coords.x, coords.y)
      if (resizeHandle) {
        const el = resizeHandle.element
        const rotated = el.rotation === 90 || el.rotation === 270
        setResizingAmenity({ elementId: el.id, corner: resizeHandle.corner })
        setResizeStartData({
          x: el.x_position || 0,
          y: el.y_position || 0,
          width: rotated ? el.height_cm : el.width_cm,
          height: rotated ? el.width_cm : el.height_cm,
          mouseX: coords.x,
          mouseY: coords.y })
        event.preventDefault?.()
        return
      }

      // Check if clicking on a wall endpoint to drag it (left-click drag)
      const wallEndpoint = findWallEndpointAtPosition(coords.x, coords.y)
      if (wallEndpoint) {
        setDraggingWallEndpoint({ elementId: wallEndpoint.element.id, endpoint: wallEndpoint.endpoint })
        setSelectedElement(wallEndpoint.element)
        setSelectedSpot(null)
        event.preventDefault?.()
        return
      }

      // Check if clicking on a floor element (wall body or amenity) to drag it
      const clickedElement = findElementAtPosition(coords.x, coords.y)
      if (clickedElement) {
        setDraggingElement(clickedElement)
        setSelectedElement(clickedElement)
        setSelectedSpot(null)
        // Calculate offset from click position
        if (clickedElement.element_type === 'wall') {
          // For walls, use x1,y1 as reference
          setElementDragOffset({ x: coords.x - (clickedElement.x1 || 0), y: coords.y - (clickedElement.y1 || 0) })
        } else {
          // For amenities, use x_position, y_position
          setElementDragOffset({ x: coords.x - (clickedElement.x_position || 0), y: coords.y - (clickedElement.y_position || 0) })
        }
        event.preventDefault?.()
        return
      }

      if (clickedSpot) {
        // Start dragging the spot (same as walls/amenities)
        setDraggingSpot(clickedSpot)
        setDragOffset({
          x: coords.x - clickedSpot.x_position,
          y: coords.y - clickedSpot.y_position })
        setDragPosition({ x: clickedSpot.x_position, y: clickedSpot.y_position })
        setSelectedSpot(clickedSpot)
        setSelectedElement(null) // Clear element selection
        // Toggle multi-selection with Ctrl/Cmd
        if (event.nativeEvent?.ctrlKey || event.nativeEvent?.metaKey) {
          setSelectedSpots(prev => {
            const newSet = new Set(prev)
            if (newSet.has(clickedSpot.id)) {
              newSet.delete(clickedSpot.id)
            } else {
              newSet.add(clickedSpot.id)
            }
            return newSet
          })
        }
        event.preventDefault?.()
      } else {
        // Clicking on empty space - deselect everything and start selection box
        setSelectedSpot(null)
        setSelectedElement(null)
        setSelectionBox({ startX: coords.x, startY: coords.y, endX: coords.x, endY: coords.y })
        setSelectedSpots(new Set()) // Clear previous selection
        event.preventDefault?.()
      }
    }
  }

  // Handle mouse/touch move for dragging (throttled for performance)
  const handleCanvasMouseMove = (event: any) => {
    if (!selectedFloor) return

    // Handle viewport panning with middle mouse button
    if (isPanning && Platform.OS === 'web') {
      const clientX = event.nativeEvent?.clientX ?? 0
      const clientY = event.nativeEvent?.clientY ?? 0
      const deltaX = clientX - panStartRef.current.x
      const deltaY = clientY - panStartRef.current.y

      // Get the native DOM elements and scroll them
      const horizontalScroll = (scrollViewRef.current as any)?.getScrollableNode?.() ||
        (scrollViewRef.current as any)?._nativeRef?.current
      const verticalScroll = (innerScrollViewRef.current as any)?.getScrollableNode?.() ||
        (innerScrollViewRef.current as any)?._nativeRef?.current

      if (horizontalScroll) {
        horizontalScroll.scrollLeft = panStartRef.current.scrollX - deltaX
      }
      if (verticalScroll) {
        verticalScroll.scrollTop = panStartRef.current.scrollY - deltaY
      }
      return
    }

    // Throttle updates to ~60fps
    const now = Date.now()
    if (now - lastMoveTimeRef.current < THROTTLE_MS) return
    lastMoveTimeRef.current = now

    const coords = getFloorCoordsFromEvent(event)
    if (!coords) return

    // Handle selection box drawing
    if (selectionBox && !isDraggingSelection && !draggingSpot) {
      setSelectionBox(prev => prev ? { ...prev, endX: coords.x, endY: coords.y } : null)
      didDragRef.current = true
      return
    }

    // Handle bulk drag
    if (isDraggingSelection && selectedSpots.size > 0) {
      const deltaX = coords.x - bulkDragOffset.x
      const deltaY = coords.y - bulkDragOffset.y
      // Snap delta to grid
      const snappedDeltaX = Math.round(deltaX / GRID_SIZE) * GRID_SIZE
      const snappedDeltaY = Math.round(deltaY / GRID_SIZE) * GRID_SIZE
      // Only update if delta actually changed
      if (bulkDragDelta?.x !== snappedDeltaX || bulkDragDelta?.y !== snappedDeltaY) {
        setBulkDragDelta({ x: snappedDeltaX, y: snappedDeltaY })
      }
      didDragRef.current = true
      return
    }

    // Handle amenity resize
    if (resizingAmenity && resizeStartData) {
      const deltaX = coords.x - resizeStartData.mouseX
      const deltaY = coords.y - resizeStartData.mouseY
      const minSize = 20 // Minimum 20cm

      setFloorElements(prev => prev.map(el => {
        if (el.id !== resizingAmenity.elementId) return el

        let newX = resizeStartData.x
        let newY = resizeStartData.y
        let newWidth = resizeStartData.width
        let newHeight = resizeStartData.height

        switch (resizingAmenity.corner) {
          case 'topLeft':
            newX = resizeStartData.x + deltaX
            newY = resizeStartData.y + deltaY
            newWidth = resizeStartData.width - deltaX
            newHeight = resizeStartData.height - deltaY
            break
          case 'topRight':
            newY = resizeStartData.y + deltaY
            newWidth = resizeStartData.width + deltaX
            newHeight = resizeStartData.height - deltaY
            break
          case 'bottomLeft':
            newX = resizeStartData.x + deltaX
            newWidth = resizeStartData.width - deltaX
            newHeight = resizeStartData.height + deltaY
            break
          case 'bottomRight':
            newWidth = resizeStartData.width + deltaX
            newHeight = resizeStartData.height + deltaY
            break
        }

        // Enforce minimum size and snap to grid
        newWidth = Math.max(minSize, Math.round(newWidth / GRID_SIZE) * GRID_SIZE)
        newHeight = Math.max(minSize, Math.round(newHeight / GRID_SIZE) * GRID_SIZE)
        newX = Math.round(newX / GRID_SIZE) * GRID_SIZE
        newY = Math.round(newY / GRID_SIZE) * GRID_SIZE

        const rotated = el.rotation === 90 || el.rotation === 270
        return {
          ...el,
          x_position: newX,
          y_position: newY,
          width_cm: rotated ? newHeight : newWidth,
          height_cm: rotated ? newWidth : newHeight }
      }))
      didDragRef.current = true
      return
    }

    // Handle wall endpoint drag
    if (draggingWallEndpoint) {
      const snappedX = Math.round(coords.x / GRID_SIZE) * GRID_SIZE
      const snappedY = Math.round(coords.y / GRID_SIZE) * GRID_SIZE
      // Clamp to floor bounds
      const clampedX = Math.max(0, Math.min(snappedX, selectedFloor.width_cm))
      const clampedY = Math.max(0, Math.min(snappedY, selectedFloor.height_cm))

      // Update the wall element in local state (live preview)
      setFloorElements(prev => prev.map(el => {
        if (el.id !== draggingWallEndpoint.elementId) return el
        if (draggingWallEndpoint.endpoint === 'start') {
          return { ...el, x1: clampedX, y1: clampedY }
        } else {
          return { ...el, x2: clampedX, y2: clampedY }
        }
      }))
      didDragRef.current = true
      return
    }

    // Handle element drag (entire wall or amenity)
    if (draggingElement) {
      const newX = coords.x - elementDragOffset.x
      const newY = coords.y - elementDragOffset.y
      const snappedX = Math.round(newX / GRID_SIZE) * GRID_SIZE
      const snappedY = Math.round(newY / GRID_SIZE) * GRID_SIZE

      setFloorElements(prev => prev.map(el => {
        if (el.id !== draggingElement.id) return el

        if (el.element_type === 'wall') {
          // Move entire wall by calculating delta from original position
          const deltaX = snappedX - (draggingElement.x1 || 0)
          const deltaY = snappedY - (draggingElement.y1 || 0)
          return {
            ...el,
            x1: (draggingElement.x1 || 0) + deltaX,
            y1: (draggingElement.y1 || 0) + deltaY,
            x2: (draggingElement.x2 || 0) + deltaX,
            y2: (draggingElement.y2 || 0) + deltaY }
        } else {
          // Move amenity
          return {
            ...el,
            x_position: snappedX,
            y_position: snappedY }
        }
      }))
      didDragRef.current = true
      return
    }

    // Handle single spot drag
    if (!draggingSpot) {
      // Not dragging anything - update hover cursor based on what's under the mouse
      if (toolMode === 'select' && Platform.OS === 'web') {
        // Check for amenity resize handles
        const resizeHandle = findAmenityResizeHandle(coords.x, coords.y)
        if (resizeHandle) {
          const corner = resizeHandle.corner
          if (corner === 'topLeft' || corner === 'bottomRight') {
            setHoverCursor('nwse-resize')
          } else {
            setHoverCursor('nesw-resize')
          }
          return
        }

        // Check for wall endpoints
        const wallEndpoint = findWallEndpointAtPosition(coords.x, coords.y)
        if (wallEndpoint) {
          setHoverCursor('move')
          return
        }

        // Check for elements (walls/amenities)
        const element = findElementAtPosition(coords.x, coords.y)
        if (element) {
          setHoverCursor('move')
          return
        }

        // Check for spots (tables)
        const spot = findSpotAtPosition(coords.x, coords.y)
        if (spot) {
          setHoverCursor('move')
          return
        }

        // Default cursor
        setHoverCursor('default')
      }
      return
    }

    didDragRef.current = true // Mark that we dragged

    // Calculate new position
    const newX = coords.x - dragOffset.x
    const newY = coords.y - dragOffset.y

    // Snap to grid
    const snappedX = Math.round(newX / GRID_SIZE) * GRID_SIZE
    const snappedY = Math.round(newY / GRID_SIZE) * GRID_SIZE

    // Clamp to floor bounds
    const spotType = draggingSpot.spot_type || spotTypes.find(t => t.id === draggingSpot.spot_type_id)
    if (!spotType) return

    const rotated = draggingSpot.rotation === 90 || draggingSpot.rotation === 270
    const spotWidth = rotated ? spotType.depth_cm : spotType.width_cm
    const spotHeight = rotated ? spotType.width_cm : spotType.depth_cm

    const clampedX = Math.max(0, Math.min(snappedX, selectedFloor.width_cm - spotWidth))
    const clampedY = Math.max(0, Math.min(snappedY, selectedFloor.height_cm - spotHeight))

    // Only update if position actually changed
    if (dragPosition?.x !== clampedX || dragPosition?.y !== clampedY) {
      setDragPosition({ x: clampedX, y: clampedY })
    }
  }

  // Handle mouse/touch up to end drag
  const handleCanvasMouseUp = async () => {
    // End panning
    if (isPanning) {
      setIsPanning(false)
      return
    }

    // Handle amenity resize end - save to database
    if (resizingAmenity) {
      const element = floorElements.find(el => el.id === resizingAmenity.elementId)
      if (element) {
        try {
          const { error } = await supabase
            .from('floor_elements')
            .update({
              x_position: element.x_position,
              y_position: element.y_position,
              width_cm: element.width_cm,
              height_cm: element.height_cm })
            .eq('id', element.id)
          if (error) throw error
        } catch (error) {
          console.error('Error saving amenity size:', error)
        }
      }
      setResizingAmenity(null)
      setResizeStartData(null)
      return
    }

    // Handle wall endpoint drag end - save to database
    if (draggingWallEndpoint) {
      const element = floorElements.find(el => el.id === draggingWallEndpoint.elementId)
      if (element) {
        try {
          const { error } = await supabase
            .from('floor_elements')
            .update({
              x1: element.x1,
              y1: element.y1,
              x2: element.x2,
              y2: element.y2 })
            .eq('id', element.id)

          if (error) throw error
        } catch (error) {
          console.error('Error saving wall position:', error)
        }
      }
      setDraggingWallEndpoint(null)
      return
    }

    // Handle element drag end - save to database
    if (draggingElement) {
      const element = floorElements.find(el => el.id === draggingElement.id)
      if (element) {
        try {
          if (element.element_type === 'wall') {
            const { error } = await supabase
              .from('floor_elements')
              .update({
                x1: element.x1,
                y1: element.y1,
                x2: element.x2,
                y2: element.y2 })
              .eq('id', element.id)
            if (error) throw error
          } else {
            const { error } = await supabase
              .from('floor_elements')
              .update({
                x_position: element.x_position,
                y_position: element.y_position })
              .eq('id', element.id)
            if (error) throw error
          }
        } catch (error) {
          console.error('Error saving element position:', error)
        }
      }
      setDraggingElement(null)
      return
    }

    // Handle bulk drag end
    if (isDraggingSelection && bulkDragDelta && selectedSpots.size > 0) {
      await finishBulkDrag()
      return
    }

    // Handle selection box end
    if (selectionBox && !isDraggingSelection) {
      finishSelectionBox()
      return
    }

    if (!draggingSpot || !dragPosition) {
      setDraggingSpot(null)
      setDragPosition(null)
      return
    }

    const spotType = draggingSpot.spot_type || spotTypes.find(t => t.id === draggingSpot.spot_type_id)
    if (!spotType) {
      setDraggingSpot(null)
      setDragPosition(null)
      return
    }

    const rotated = draggingSpot.rotation === 90 || draggingSpot.rotation === 270
    const spotWidth = rotated ? spotType.depth_cm : spotType.width_cm
    const spotHeight = rotated ? spotType.width_cm : spotType.depth_cm

    // Find nearest available position (auto-snap to avoid overlap)
    const finalPosition = findNearestAvailablePosition(
      dragPosition.x,
      dragPosition.y,
      spotWidth,
      spotHeight,
      draggingSpot.id
    )

    if (!finalPosition) {
      // No position found, revert
      setDraggingSpot(null)
      setDragPosition(null)
      return
    }

    // Update spots array with new position
    setSpots(spots.map(s =>
      s.id === draggingSpot.id ? { ...s, x_position: finalPosition.x, y_position: finalPosition.y } : s
    ))

    // Save to database
    await updateSpotPosition(draggingSpot.id, finalPosition.x, finalPosition.y)

    setDraggingSpot(null)
    setDragPosition(null)
  }

  // Finish selection box - select all spots within the box
  const finishSelectionBox = () => {
    if (!selectionBox) return

    const minX = Math.min(selectionBox.startX, selectionBox.endX)
    const maxX = Math.max(selectionBox.startX, selectionBox.endX)
    const minY = Math.min(selectionBox.startY, selectionBox.endY)
    const maxY = Math.max(selectionBox.startY, selectionBox.endY)

    const selected = new Set<string>()
    for (const spot of spots) {
      const spotType = spot.spot_type || spotTypes.find(t => t.id === spot.spot_type_id)
      if (!spotType) continue

      const rotated = spot.rotation === 90 || spot.rotation === 270
      const width = rotated ? spotType.depth_cm : spotType.width_cm
      const height = rotated ? spotType.width_cm : spotType.depth_cm

      // Check if spot is within or intersects selection box
      const spotMinX = spot.x_position
      const spotMaxX = spot.x_position + width
      const spotMinY = spot.y_position
      const spotMaxY = spot.y_position + height

      const intersects = !(spotMaxX < minX || spotMinX > maxX || spotMaxY < minY || spotMinY > maxY)
      if (intersects) {
        selected.add(spot.id)
      }
    }

    setSelectedSpots(selected)
    setSelectionBox(null)
  }

  // Finish bulk drag - move all selected spots
  const finishBulkDrag = async () => {
    if (!bulkDragDelta || selectedSpots.size === 0) {
      setIsDraggingSelection(false)
      setBulkDragDelta(null)
      return
    }

    // Calculate new positions for all selected spots
    const updates: { id: string, x: number, y: number }[] = []
    const selectedSpotsList = spots.filter(s => selectedSpots.has(s.id))

    for (const spot of selectedSpotsList) {
      const spotType = spot.spot_type || spotTypes.find(t => t.id === spot.spot_type_id)
      if (!spotType) continue

      const rotated = spot.rotation === 90 || spot.rotation === 270
      const width = rotated ? spotType.depth_cm : spotType.width_cm
      const height = rotated ? spotType.width_cm : spotType.depth_cm

      // Calculate new position
      let newX = spot.x_position + bulkDragDelta.x
      let newY = spot.y_position + bulkDragDelta.y

      // Snap to grid
      newX = Math.round(newX / GRID_SIZE) * GRID_SIZE
      newY = Math.round(newY / GRID_SIZE) * GRID_SIZE

      // Clamp to bounds
      if (selectedFloor) {
        newX = Math.max(0, Math.min(newX, selectedFloor.width_cm - width))
        newY = Math.max(0, Math.min(newY, selectedFloor.height_cm - height))
      }

      updates.push({ id: spot.id, x: newX, y: newY })
    }

    // Update local state
    setSpots(spots.map(s => {
      const update = updates.find(u => u.id === s.id)
      if (update) {
        return { ...s, x_position: update.x, y_position: update.y }
      }
      return s
    }))

    // Save to database
    for (const update of updates) {
      try {
        await supabase
          .from('spots')
          .update({ x_position: update.x, y_position: update.y })
          .eq('id', update.id)
      } catch (error) {
        console.error('Error updating spot position:', error)
      }
    }

    setIsDraggingSelection(false)
    setBulkDragDelta(null)
  }

  // Handle spot press
  const handleSpotPress = (spot: Spot) => {
    if (toolMode === 'select') {
      setSelectedSpot(spot)
      // No modal on click - just select
    } else if (toolMode === 'number') {
      const newNumber = `${numberingPrefix}${numberingCounter}`
      updateSpotNumber(spot.id, newNumber)
      setNumberingCounter(prev => prev + 1)
    }
  }

  // Memoized grid lines - only recompute when floor or zoom changes
  const gridLines = useMemo(() => {
    if (!selectedFloor) return null

    const scale = getScale()
    const canvasWidth = selectedFloor.width_cm * scale
    const canvasHeight = selectedFloor.height_cm * scale
    const lines: React.ReactElement[] = []

    // Determine grid step based on floor size and zoom
    // For very large floors, use larger grid steps
    const floorArea = selectedFloor.width_cm * selectedFloor.height_cm
    const maxFineGridLines = 200 // Cap fine grid lines for performance

    // Calculate appropriate fine grid step
    let fineGridStep = GRID_SIZE // 10cm default
    if (floorArea > 1000000) { // > 100m²
      fineGridStep = 50 // 50cm for medium floors
    }
    if (floorArea > 10000000) { // > 1000m²
      fineGridStep = 100 // 1m for large floors (skip fine grid, only show bold)
    }

    // Only show fine grid when zoomed in enough (scale > 0.3)
    const showFineGrid = scale > 0.3 && fineGridStep < 100

    // Fine grid lines (only when zoomed in and floor is reasonable size)
    if (showFineGrid) {
      const fineLineCountX = Math.floor(selectedFloor.width_cm / fineGridStep) + 1
      const fineLineCountY = Math.floor(selectedFloor.height_cm / fineGridStep) + 1

      // Only render if total lines won't exceed max
      if (fineLineCountX + fineLineCountY <= maxFineGridLines) {
        for (let i = 0; i <= selectedFloor.width_cm; i += fineGridStep) {
          lines.push(
            <Line
              key={`v10-${i}`}
              x1={i * scale}
              y1={0}
              x2={i * scale}
              y2={canvasHeight}
              stroke="#f0f0f0"
              strokeWidth={0.5}
            />
          )
        }
        for (let i = 0; i <= selectedFloor.height_cm; i += fineGridStep) {
          lines.push(
            <Line
              key={`h10-${i}`}
              x1={0}
              y1={i * scale}
              x2={canvasWidth}
              y2={i * scale}
              stroke="#f0f0f0"
              strokeWidth={0.5}
            />
          )
        }
      }
    }

    // Bold grid lines (1m) - always show but limit for very large floors
    const boldGridStep = floorArea > 100000000 ? 1000 : 100 // 10m steps for huge floors
    const maxBoldLines = 100
    const boldLineCountX = Math.floor(selectedFloor.width_cm / boldGridStep) + 1
    const boldLineCountY = Math.floor(selectedFloor.height_cm / boldGridStep) + 1

    if (boldLineCountX + boldLineCountY <= maxBoldLines) {
      for (let i = 0; i <= selectedFloor.width_cm; i += boldGridStep) {
        lines.push(
          <Line
            key={`v100-${i}`}
            x1={i * scale}
            y1={0}
            x2={i * scale}
            y2={canvasHeight}
            stroke="#d0d0d0"
            strokeWidth={1}
          />
        )
      }
      for (let i = 0; i <= selectedFloor.height_cm; i += boldGridStep) {
        lines.push(
          <Line
            key={`h100-${i}`}
            x1={0}
            y1={i * scale}
            x2={canvasWidth}
            y2={i * scale}
            stroke="#d0d0d0"
            strokeWidth={1}
          />
        )
      }
    }

    return lines
  }, [selectedFloor?.id, selectedFloor?.width_cm, selectedFloor?.height_cm, zoomLevel])

  // Memoized ruler marks
  const rulerMarks = useMemo(() => {
    if (!selectedFloor) return null

    const scale = getScale()
    const marks: React.ReactElement[] = []

    // Determine ruler step based on floor size
    const floorArea = selectedFloor.width_cm * selectedFloor.height_cm
    let rulerStep = 100 // 1m default
    if (floorArea > 10000000) rulerStep = 500 // 5m for large floors
    if (floorArea > 100000000) rulerStep = 1000 // 10m for huge floors

    // Top ruler (horizontal)
    for (let i = 0; i <= selectedFloor.width_cm; i += rulerStep) {
      marks.push(
        <View key={`ruler-h-${i}`} style={[styles.rulerMark, { left: 40 + i * scale }]}>
          <Text style={styles.rulerText}>{i / 100}m</Text>
        </View>
      )
    }

    // Left ruler (vertical)
    for (let i = 0; i <= selectedFloor.height_cm; i += rulerStep) {
      marks.push(
        <View key={`ruler-v-${i}`} style={[styles.rulerMarkV, { top: 40 + i * scale }]}>
          <Text style={styles.rulerText}>{i / 100}m</Text>
        </View>
      )
    }

    return marks
  }, [selectedFloor?.id, selectedFloor?.width_cm, selectedFloor?.height_cm, zoomLevel])

  // Render the floor plan canvas
  const renderCanvas = () => {
    if (!selectedFloor) {
      return (
        <View style={styles.emptyCanvas}>
          <Text style={styles.emptyCanvasText}>
            {t('no_floor_selected') || 'No floor selected'}
          </Text>
          <Text style={styles.emptyCanvasHint}>
            {t('create_floor_hint') || 'Create a floor to start designing your layout'}
          </Text>
          <TouchableOpacity
            style={styles.addFloorButton}
            onPress={() => setFloorModalVisible(true)}
          >
            <Text style={styles.addFloorButtonText}>
              + {t('add_floor') || 'Add Floor'}
            </Text>
          </TouchableOpacity>
        </View>
      )
    }

    const scale = getScale()
    const canvasWidth = selectedFloor.width_cm * scale
    const canvasHeight = selectedFloor.height_cm * scale

    return (
      <View
        style={styles.canvasContainer}
        onLayout={(e) => {
          const { width, height } = e.nativeEvent.layout
          setContainerSize({ width, height })
        }}
      >
        {/* Rulers (memoized) */}
        <View style={styles.rulerContainer}>
          {rulerMarks}
        </View>

        <ScrollView
          ref={scrollViewRef}
          horizontal
          style={styles.canvasScroll}
          contentContainerStyle={styles.canvasScrollContent}
          showsHorizontalScrollIndicator={true}
          scrollEnabled={!draggingSpot && !selectionBox && !isDraggingSelection && !isPanning}
        >
          <ScrollView
            ref={innerScrollViewRef}
            contentContainerStyle={styles.canvasScrollContent}
            showsVerticalScrollIndicator={true}
            scrollEnabled={!draggingSpot && !selectionBox && !isDraggingSelection && !isPanning}
          >
            <View
              style={[styles.canvas, { width: canvasWidth, height: canvasHeight, cursor: isPanning ? 'grabbing' : ((draggingSpot || isDraggingSelection || draggingElement || draggingWallEndpoint || resizingAmenity) ? 'grabbing' : (selectionBox ? 'crosshair' : (toolMode === 'select' ? hoverCursor : 'crosshair'))) } as any]}
              onTouchStart={handleCanvasMouseDown}
              onTouchMove={handleCanvasMouseMove}
              onTouchEnd={handleCanvasMouseUp}
              {...(Platform.OS === 'web' ? {
                onMouseDown: handleCanvasMouseDown,
                onMouseMove: handleCanvasMouseMove,
                onMouseUp: handleCanvasMouseUp,
                onMouseLeave: handleCanvasMouseUp,
                onClick: handleCanvasPress,
                onDoubleClick: handleCanvasDoubleClick,
                onContextMenu: (e: any) => e.preventDefault(), // Prevent right-click menu
              } : {
                onPress: handleCanvasPress })}
            >
              <Svg width={canvasWidth} height={canvasHeight} style={{ pointerEvents: 'none' } as any}>
                {/* Grid lines (memoized) */}
                {gridLines}

                {/* Floor elements (walls and amenities) - render before spots so spots appear on top */}
                {floorElements.map((element) => {
                  const isSelected = selectedElement?.id === element.id

                  if (element.element_type === 'wall') {
                    // Render wall as a thick line
                    if (element.x1 === undefined || element.y1 === undefined ||
                        element.x2 === undefined || element.y2 === undefined) return null

                    return (
                      <G key={element.id}>
                        <Line
                          x1={element.x1 * scale}
                          y1={element.y1 * scale}
                          x2={element.x2 * scale}
                          y2={element.y2 * scale}
                          stroke={element.color || '#333'}
                          strokeWidth={(element.wall_thickness || 10) * scale}
                          strokeLinecap="square"
                          opacity={0.9}
                        />
                        {/* Only show endpoint handles when wall is selected */}
                        {isSelected && (
                          <>
                            <Circle
                              cx={element.x1 * scale}
                              cy={element.y1 * scale}
                              r={10}
                              fill="#00BFFF"
                              stroke="#fff"
                              strokeWidth={2}
                            />
                            <Circle
                              cx={element.x2 * scale}
                              cy={element.y2 * scale}
                              r={10}
                              fill="#00BFFF"
                              stroke="#fff"
                              strokeWidth={2}
                            />
                          </>
                        )}
                      </G>
                    )
                  } else {
                    // Render amenity as a rectangle with icon
                    if (element.x_position === undefined || element.y_position === undefined) return null

                    const rotated = element.rotation === 90 || element.rotation === 270
                    const width = (rotated ? element.height_cm : element.width_cm) * scale
                    const height = (rotated ? element.width_cm : element.height_cm) * scale
                    const x = element.x_position * scale
                    const y = element.y_position * scale

                    const amenityPreset = AMENITY_PRESETS.find(a => a.type === element.element_type)
                    const icon = amenityPreset?.icon || '❓'

                    return (
                      <G key={element.id}>
                        <Rect
                          x={x}
                          y={y}
                          width={width}
                          height={height}
                          fill={element.color || '#888'}
                          opacity={0.7}
                          stroke={isSelected ? '#00BFFF' : '#444'}
                          strokeWidth={isSelected ? 2 : 1}
                          rx={4}
                        />
                        <SvgText
                          x={x + width / 2}
                          y={y + height / 2}
                          fontSize={Math.min(width, height) * 0.5}
                          textAnchor="middle"
                          alignmentBaseline="middle"
                          pointerEvents="none"
                        >
                          {icon}
                        </SvgText>
                        {element.label && (
                          <SvgText
                            x={x + width / 2}
                            y={y + height + 12}
                            fill="#666"
                            fontSize={10}
                            textAnchor="middle"
                            pointerEvents="none"
                          >
                            {element.label}
                          </SvgText>
                        )}
                        {/* Resize handles for selected amenities */}
                        {isSelected && (
                          <>
                            <Circle cx={x} cy={y} r={8} fill="#00BFFF" stroke="#fff" strokeWidth={2} />
                            <Circle cx={x + width} cy={y} r={8} fill="#00BFFF" stroke="#fff" strokeWidth={2} />
                            <Circle cx={x} cy={y + height} r={8} fill="#00BFFF" stroke="#fff" strokeWidth={2} />
                            <Circle cx={x + width} cy={y + height} r={8} fill="#00BFFF" stroke="#fff" strokeWidth={2} />
                          </>
                        )}
                      </G>
                    )
                  }
                })}

                {/* Wall drawing preview */}
                {wallDrawing && toolMode === 'wall' && (
                  <G>
                    <Circle
                      cx={wallDrawing.x1 * scale}
                      cy={wallDrawing.y1 * scale}
                      r={6}
                      fill="#FF4500"
                      stroke="#fff"
                      strokeWidth={2}
                    />
                    <SvgText
                      x={wallDrawing.x1 * scale + 10}
                      y={wallDrawing.y1 * scale - 10}
                      fill="#FF4500"
                      fontSize={12}
                      pointerEvents="none"
                    >
                      {t('start_point') || 'Start'}
                    </SvgText>
                  </G>
                )}

                {/* Spots */}
                {spots.map((spot) => {
                  const spotType = spot.spot_type || spotTypes.find(t => t.id === spot.spot_type_id)
                  if (!spotType) return null

                  const isSelected = selectedSpot?.id === spot.id
                  const isMultiSelected = selectedSpots.has(spot.id)
                  const isDragging = draggingSpot?.id === spot.id
                  const isBulkDragging = isMultiSelected && isDraggingSelection && bulkDragDelta
                  const width = spotType.width_cm * scale
                  const height = spotType.depth_cm * scale

                  // Calculate position
                  let baseX = spot.x_position
                  let baseY = spot.y_position

                  // Use drag position if this spot is being single-dragged
                  if (isDragging && dragPosition) {
                    baseX = dragPosition.x
                    baseY = dragPosition.y
                  }
                  // Apply bulk drag delta if this spot is being bulk-dragged
                  else if (isBulkDragging) {
                    baseX = spot.x_position + bulkDragDelta.x
                    baseY = spot.y_position + bulkDragDelta.y
                  }

                  const x = baseX * scale
                  const y = baseY * scale

                  // Handle rotation by swapping dimensions
                  const rotated = spot.rotation === 90 || spot.rotation === 270
                  const displayWidth = rotated ? height : width
                  const displayHeight = rotated ? width : height

                  // Determine visual state
                  const isBeingMoved = isDragging || isBulkDragging

                  return (
                    <G key={spot.id} opacity={isBeingMoved ? 0.7 : 1}>
                      <Rect
                        x={x}
                        y={y}
                        width={displayWidth}
                        height={displayHeight}
                        fill={spotType.color}
                        opacity={spot.is_available ? 0.85 : 0.4}
                        stroke={isBeingMoved ? '#FFD700' : (isMultiSelected ? '#00BFFF' : (isSelected ? '#000' : '#fff'))}
                        strokeWidth={isBeingMoved ? 2 : (isMultiSelected || isSelected ? 2 : 1)}
                        strokeDasharray={isBeingMoved ? '5,5' : undefined}
                        rx={4}
                      />
                      <SvgText
                        x={x + displayWidth / 2}
                        y={y + displayHeight / 2}
                        fill="#fff"
                        fontSize={Math.min(displayWidth, displayHeight) * 0.35}
                        fontWeight="bold"
                        textAnchor="middle"
                        alignmentBaseline="middle"
                        pointerEvents="none"
                      >
                        {spot.spot_number}
                      </SvgText>
                      {/* Show dimensions on hover/select */}
                      {(isSelected || isDragging) && (
                        <SvgText
                          x={x + displayWidth / 2}
                          y={y + displayHeight + 12}
                          fill="#666"
                          fontSize={10}
                          textAnchor="middle"
                          pointerEvents="none"
                        >
                          {spotType.width_cm}×{spotType.depth_cm}cm
                        </SvgText>
                      )}
                    </G>
                  )
                })}

                {/* Selection box */}
                {selectionBox && (
                  <Rect
                    x={Math.min(selectionBox.startX, selectionBox.endX) * scale}
                    y={Math.min(selectionBox.startY, selectionBox.endY) * scale}
                    width={Math.abs(selectionBox.endX - selectionBox.startX) * scale}
                    height={Math.abs(selectionBox.endY - selectionBox.startY) * scale}
                    fill="rgba(0, 191, 255, 0.1)"
                    stroke="#00BFFF"
                    strokeWidth={2}
                    strokeDasharray="5,5"
                  />
                )}
              </Svg>
            </View>
          </ScrollView>
        </ScrollView>

        {/* Zoom controls */}
        <View style={styles.zoomControls}>
          <TouchableOpacity style={styles.zoomButton} onPress={zoomIn}>
            <Plus size={20} color="#333" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.zoomButton} onPress={resetZoom}>
            <Text style={styles.zoomButtonTextSmall}>{t('fit') || 'Fit'}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.zoomButton} onPress={zoomOut}>
            <Minus size={20} color="#333" />
          </TouchableOpacity>
        </View>

        {/* Canvas info */}
        <View style={styles.canvasInfo}>
          <Text style={styles.dimensionLabel}>
            {selectedFloor.width_cm / 100}m × {selectedFloor.height_cm / 100}m
          </Text>
        </View>

        {/* Selection info */}
        {selectedSpots.size > 0 && (
          <View style={styles.selectionInfo}>
            <Text style={styles.selectionInfoText}>
              {selectedSpots.size} {t('selected') || 'selected'}
            </Text>
            <TouchableOpacity
              style={styles.copyButton}
              onPress={() => copySpot()}
            >
              <Clipboard size={18} color="#fff" />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.clearSelectionButton}
              onPress={() => setSelectedSpots(new Set())}
            >
              <X size={18} color="#fff" />
            </TouchableOpacity>
          </View>
        )}

        {/* Copied spots indicator */}
        {copiedSpots.length > 0 && (
          <View style={[styles.selectionInfo, styles.copiedInfo]}>
            <Text style={styles.selectionInfoText}>
              {copiedSpots.length} {t('copied') || 'copied'} - {t('click_to_paste') || 'click to paste'}
            </Text>
            <TouchableOpacity
              style={styles.clearSelectionButton}
              onPress={() => { setCopiedSpots([]); setToolMode('select') }}
            >
              <X size={18} color="#fff" />
            </TouchableOpacity>
          </View>
        )}
      </View>
    )
  }

  if (loading) {
    return (
      <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
    )
  }

  // Access denied - not an admin
  if (isAdmin === false) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Button variant="ghost" size="sm" onPress={() => navigation.goBack()}><ArrowLeft size={20} color="#748C70" /></Button>
          <Text style={styles.headerTitle}>{t('floor_plan_editor') || 'Floor Plan'}</Text>
          <View style={{ width: 40 }} />
        </View>
        <View style={styles.loadingContainer}>
          <Text style={{ fontSize: 16, color: theme.colors.text.secondary, textAlign: 'center', padding: 20 }}>
            {t('access_denied') || 'You do not have permission to edit this floor plan'}
          </Text>
          <TouchableOpacity
            style={{ marginTop: 16, paddingVertical: 12, paddingHorizontal: 24, backgroundColor: merchantColors.primary, borderRadius: 8 }}
            onPress={() => navigation.goBack()}
          >
            <Text style={{ color: '#fff', fontWeight: '600' }}>{t('go_back') || 'Go Back'}</Text>
          </TouchableOpacity>
        </View>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      {/* Header with floor tabs */}
      <View style={styles.header}>
        <Button variant="ghost" size="sm" onPress={() => navigation.goBack()}><ArrowLeft size={20} color="#748C70" /></Button>
        <Text style={styles.headerTitle}>{t('floor_plan_editor') || 'Floor Plan'}</Text>
        <View style={styles.headerFloorTabs}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.floorTabsContent}>
            {floors.map((floor) => (
              <TouchableOpacity
                key={floor.id}
                style={[styles.floorTab, selectedFloor?.id === floor.id && styles.floorTabActive]}
                onPress={() => setSelectedFloor(floor)}
                onLongPress={() => openFloorEditModal(floor)}
                delayLongPress={500}
              >
                <Text style={[styles.floorTabText, selectedFloor?.id === floor.id && styles.floorTabTextActive]}>
                  {floor.name}
                </Text>
                {selectedFloor?.id === floor.id && (
                  <TouchableOpacity
                    style={styles.floorEditButton}
                    onPress={() => openFloorEditModal(floor)}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  >
                    <Text style={styles.floorEditButtonText}>✎</Text>
                  </TouchableOpacity>
                )}
              </TouchableOpacity>
            ))}
            <TouchableOpacity
              style={styles.addFloorTab}
              onPress={() => setFloorModalVisible(true)}
            >
              <Plus size={18} color={merchantColors.primary} />
            </TouchableOpacity>
          </ScrollView>
        </View>
      </View>

      {/* Toolbar */}
      <View style={styles.toolbar}>
        <View style={styles.toolGroup}>
          <TouchableOpacity
            style={[styles.toolButton, toolMode === 'select' && styles.toolButtonActive]}
            onPress={() => { setToolMode('select'); setCopiedSpots([]) }}
          >
            <MousePointer2 size={20} color={toolMode === 'select' ? merchantColors.primary : '#666'} />
            <Text style={[styles.toolButtonLabel, toolMode === 'select' && styles.toolButtonLabelActive]}>
              Valitse
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.toolButton, toolMode === 'add' && styles.toolButtonActive]}
            onPress={() => setToolMode('add')}
          >
            <CirclePlus size={20} color={toolMode === 'add' ? merchantColors.primary : '#666'} />
            <Text style={[styles.toolButtonLabel, toolMode === 'add' && styles.toolButtonLabelActive]}>
              Pöytä
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.toolButton, toolMode === 'number' && styles.toolButtonActive]}
            onPress={() => { setToolMode('number'); setNumberingCounter(1) }}
          >
            <Hash size={20} color={toolMode === 'number' ? merchantColors.primary : '#666'} />
            <Text style={[styles.toolButtonLabel, toolMode === 'number' && styles.toolButtonLabelActive]}>
              Numeroi
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.toolButton, toolMode === 'wall' && styles.toolButtonActive]}
            onPress={() => { setToolMode('wall'); setWallDrawing(null) }}
          >
            <Ruler size={20} color={toolMode === 'wall' ? merchantColors.primary : '#666'} />
            <Text style={[styles.toolButtonLabel, toolMode === 'wall' && styles.toolButtonLabelActive]}>
              Seinät
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.toolButton, toolMode === 'amenity' && styles.toolButtonActive]}
            onPress={() => { setToolMode('amenity'); setSelectedAmenityType(AMENITY_PRESETS[0]) }}
          >
            <Text style={styles.toolButtonText}>🚪</Text>
            <Text style={[styles.toolButtonLabel, toolMode === 'amenity' && styles.toolButtonLabelActive]}>
              Palvelut
            </Text>
          </TouchableOpacity>
        </View>

        {/* Spot type selector (visible in add mode) */}
        {toolMode === 'add' && (
          <View style={styles.spotTypeSelectorContainer}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.spotTypeSelector}>
              {spotTypes.map((type) => (
                <TouchableOpacity
                  key={type.id}
                  style={[
                    styles.spotTypeChip,
                    { borderColor: type.color },
                    selectedSpotType?.id === type.id && { backgroundColor: type.color },
                  ]}
                  onPress={() => { setSelectedSpotType(type); setCopiedSpots([]) }}
                >
                  <View style={[styles.spotTypeColorDot, { backgroundColor: type.color }]} />
                  <Text style={[
                    styles.spotTypeChipText,
                    selectedSpotType?.id === type.id && styles.spotTypeChipTextActive
                  ]}>
                    {type.name} ({type.width_cm}×{type.depth_cm})
                  </Text>
                </TouchableOpacity>
              ))}
              <TouchableOpacity
                style={styles.addSpotTypeChip}
                onPress={() => setQuickAddVisible(true)}
              >
                <Text style={styles.addSpotTypeChipText}>+ {t('new_table') || 'New Table'}</Text>
              </TouchableOpacity>
            </ScrollView>
            {copiedSpots.length > 0 && (
              <View style={styles.copyIndicator}>
                <Clipboard size={14} color="#fff" style={{ marginRight: 6 }} />
                <Text style={styles.copyIndicatorText}>
                  {t('pasting') || 'Pasting'}: {copiedSpots.length} {copiedSpots.length === 1 ? copiedSpots[0].spot_number : t('spots') || 'spots'}
                </Text>
                <TouchableOpacity onPress={() => setCopiedSpots([])}>
                  <X size={16} color="#fff" />
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}

        {/* Numbering options */}
        {toolMode === 'number' && (
          <View style={styles.numberingOptions}>
            <Text style={styles.numberingLabel}>{t('prefix') || 'Prefix'}:</Text>
            <TextInput
              style={styles.numberingInput}
              value={numberingPrefix}
              onChangeText={setNumberingPrefix}
              placeholder="A"
              placeholderTextColor="#999"
            />
            <Text style={styles.numberingLabel}>{t('start') || 'Start'}:</Text>
            <TextInput
              style={styles.numberingInput}
              value={String(numberingCounter)}
              onChangeText={(v) => setNumberingCounter(parseInt(v) || 1)}
              keyboardType="numeric"
              placeholder="1"
              placeholderTextColor="#999"
            />
            <Text style={styles.numberingPreview}>{t('next') || 'Next'}: {numberingPrefix}{numberingCounter}</Text>
          </View>
        )}

        {/* Wall drawing options */}
        {toolMode === 'wall' && (
          <View style={styles.wallOptions}>
            <Text style={styles.wallInstructions}>
              {wallDrawing
                ? (t('click_end_point') || 'Click end point')
                : (t('click_start_point') || 'Click start point')}
            </Text>
            <View style={styles.wallControls}>
              <Text style={styles.numberingLabel}>{t('thickness') || 'Thickness'} (cm):</Text>
              <TextInput
                style={styles.numberingInput}
                value={String(wallThickness)}
                onChangeText={(v) => setWallThickness(parseInt(v) || 10)}
                keyboardType="numeric"
                placeholder="10"
                placeholderTextColor="#999"
              />
              <Text style={styles.numberingLabel}>{t('color') || 'Color'}:</Text>
              <View style={styles.wallColorPicker}>
                {['#333333', '#666666', '#8B4513', '#2F4F4F', '#800000'].map(color => (
                  <TouchableOpacity
                    key={color}
                    style={[
                      styles.wallColorOption,
                      { backgroundColor: color },
                      wallColor === color && styles.wallColorOptionSelected
                    ]}
                    onPress={() => setWallColor(color)}
                  />
                ))}
              </View>
            </View>
            {wallDrawing && (
              <TouchableOpacity
                style={styles.cancelWallButton}
                onPress={() => setWallDrawing(null)}
              >
                <Text style={styles.cancelWallText}>{t('cancel') || 'Cancel'}</Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Amenity selector */}
        {toolMode === 'amenity' && (
          <View style={styles.amenitySelector}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {AMENITY_PRESETS.map((amenity) => (
                <TouchableOpacity
                  key={amenity.type}
                  style={[
                    styles.amenityChip,
                    { borderColor: amenity.color },
                    selectedAmenityType?.type === amenity.type && { backgroundColor: amenity.color },
                  ]}
                  onPress={() => setSelectedAmenityType(amenity)}
                >
                  <Text style={styles.amenityIcon}>{amenity.icon}</Text>
                  <Text style={[
                    styles.amenityLabel,
                    selectedAmenityType?.type === amenity.type && styles.amenityLabelSelected
                  ]}>
                    {t(amenity.labelKey) || amenity.labelKey}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            {selectedAmenityType && (
              <Text style={styles.amenityHint}>
                {t('click_to_place') || 'Click to place'} {t(selectedAmenityType.labelKey) || selectedAmenityType.labelKey}
              </Text>
            )}
          </View>
        )}
      </View>

      {/* Canvas */}
      <View style={styles.canvasWrapper}>
        {renderCanvas()}
      </View>

      {/* Stats bar */}
      <View style={styles.statsBar}>
        <Text style={styles.statText}>
          {t('spots') || 'Spots'}: {spots.length}
        </Text>
        <Text style={styles.statText}>
          {t('available') || 'Available'}: {spots.filter(s => s.is_available).length}
        </Text>
        {spotTypes.length > 0 && (
          <Text style={styles.statText}>
            {t('types') || 'Types'}: {spotTypes.length}
          </Text>
        )}
      </View>

      {/* Floor Modal */}
      <Modal visible={floorModalVisible} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{t('add_floor') || 'Add Floor'}</Text>
              <TouchableOpacity onPress={() => setFloorModalVisible(false)}>
                <X size={24} color="#999" />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalForm}>
              <Text style={styles.label}>{t('floor_name') || 'Floor Name'} *</Text>
              <TextInput
                style={styles.input}
                value={floorForm.name}
                onChangeText={(v) => setFloorForm({ ...floorForm, name: v })}
                placeholder={t('floor_name_placeholder') || 'e.g. Main Hall, Floor 1'}
                placeholderTextColor="#999"
              />

              <Text style={styles.sectionTitle}>{t('floor_dimensions') || 'Floor Area Dimensions'}</Text>
              <View style={styles.row}>
                <View style={styles.col}>
                  <Text style={styles.label}>{t('width_m') || 'Width (m)'}</Text>
                  <TextInput
                    style={styles.input}
                    value={floorForm.width_m}
                    onChangeText={(v) => setFloorForm({ ...floorForm, width_m: v })}
                    keyboardType="decimal-pad"
                    placeholder="10"
                    placeholderTextColor="#999"
                  />
                </View>
                <View style={styles.col}>
                  <Text style={styles.label}>{t('depth_m') || 'Depth (m)'}</Text>
                  <TextInput
                    style={styles.input}
                    value={floorForm.depth_m}
                    onChangeText={(v) => setFloorForm({ ...floorForm, depth_m: v })}
                    keyboardType="decimal-pad"
                    placeholder="8"
                    placeholderTextColor="#999"
                  />
                </View>
              </View>
              <Text style={styles.hint}>
                {t('floor_dimensions_hint_m') || 'Enter the actual floor area dimensions in meters.'}
              </Text>
            </ScrollView>

            <TouchableOpacity
              style={[styles.saveButton, saving && styles.buttonDisabled]}
              onPress={createFloor}
              disabled={saving}
            >
              {saving ? (
                <Spinner color="#fff" />
              ) : (
                <Text style={styles.saveButtonText}>{t('create_floor') || 'Create Floor'}</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Floor Edit Modal */}
      <Modal visible={floorEditModalVisible} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{t('edit_floor') || 'Edit Floor'}</Text>
              <TouchableOpacity onPress={() => setFloorEditModalVisible(false)}>
                <X size={24} color="#999" />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalForm}>
              <Text style={styles.label}>{t('floor_name') || 'Floor Name'} *</Text>
              <TextInput
                style={styles.input}
                value={floorEditForm.name}
                onChangeText={(v) => setFloorEditForm({ ...floorEditForm, name: v })}
                placeholder={t('floor_name_placeholder') || 'e.g. Main Hall, Floor 1'}
                placeholderTextColor="#999"
              />

              <Text style={styles.sectionTitle}>{t('floor_dimensions') || 'Floor Area Dimensions'}</Text>
              <View style={styles.row}>
                <View style={styles.col}>
                  <Text style={styles.label}>{t('width_m') || 'Width (m)'}</Text>
                  <TextInput
                    style={styles.input}
                    value={floorEditForm.width_m}
                    onChangeText={(v) => setFloorEditForm({ ...floorEditForm, width_m: v })}
                    keyboardType="decimal-pad"
                    placeholder="10"
                    placeholderTextColor="#999"
                  />
                </View>
                <View style={styles.col}>
                  <Text style={styles.label}>{t('depth_m') || 'Depth (m)'}</Text>
                  <TextInput
                    style={styles.input}
                    value={floorEditForm.depth_m}
                    onChangeText={(v) => setFloorEditForm({ ...floorEditForm, depth_m: v })}
                    keyboardType="decimal-pad"
                    placeholder="8"
                    placeholderTextColor="#999"
                  />
                </View>
              </View>
              <Text style={styles.hint}>
                {t('floor_dimensions_hint_m') || 'Enter the actual floor area dimensions in meters.'}
              </Text>
            </ScrollView>

            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[styles.deleteFloorButton, saving && styles.buttonDisabled]}
                onPress={() => deleteFloor(floorEditForm.id)}
                disabled={saving}
              >
                <Text style={styles.deleteFloorButtonText}>{t('delete_floor') || 'Delete Floor'}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.saveButton, styles.saveButtonFlex, saving && styles.buttonDisabled]}
                onPress={updateFloor}
                disabled={saving}
              >
                {saving ? (
                  <Spinner color="#fff" />
                ) : (
                  <Text style={styles.saveButtonText}>{t('save_changes') || 'Save Changes'}</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Quick Add Spot Type Modal */}
      <Modal visible={quickAddVisible} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{t('add_table_type') || 'Add Table Type'}</Text>
              <TouchableOpacity onPress={() => setQuickAddVisible(false)}>
                <X size={24} color="#999" />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalForm}>
              {/* Presets */}
              <Text style={styles.sectionTitle}>{t('presets') || 'Quick Presets'}</Text>
              <View style={styles.presetRow}>
                {SPOT_TYPE_PRESETS.map((preset, idx) => (
                  <TouchableOpacity
                    key={idx}
                    style={styles.presetButton}
                    onPress={() => setQuickAddForm({
                      ...quickAddForm,
                      name: t(preset.nameKey) || preset.nameKey,
                      width_cm: String(preset.width_cm),
                      depth_cm: String(preset.depth_cm) })}
                  >
                    <Text style={styles.presetButtonText}>{t(preset.nameKey) || preset.nameKey}</Text>
                    <Text style={styles.presetButtonSize}>{preset.width_cm}×{preset.depth_cm}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              <Text style={styles.sectionTitle}>{t('custom') || 'Custom'}</Text>

              <Text style={styles.label}>{t('type_name') || 'Type Name'} *</Text>
              <TextInput
                style={styles.input}
                value={quickAddForm.name}
                onChangeText={(v) => setQuickAddForm({ ...quickAddForm, name: v })}
                placeholder={t('table') || 'Table'}
                placeholderTextColor="#999"
              />

              <View style={styles.row}>
                <View style={styles.col}>
                  <Text style={styles.label}>{t('width_cm') || 'Width (cm)'}</Text>
                  <TextInput
                    style={styles.input}
                    value={quickAddForm.width_cm}
                    onChangeText={(v) => setQuickAddForm({ ...quickAddForm, width_cm: v })}
                    keyboardType="numeric"
                    placeholder="120"
                    placeholderTextColor="#999"
                  />
                </View>
                <View style={styles.col}>
                  <Text style={styles.label}>{t('depth_cm') || 'Depth (cm)'}</Text>
                  <TextInput
                    style={styles.input}
                    value={quickAddForm.depth_cm}
                    onChangeText={(v) => setQuickAddForm({ ...quickAddForm, depth_cm: v })}
                    keyboardType="numeric"
                    placeholder="60"
                    placeholderTextColor="#999"
                  />
                </View>
              </View>

              <Text style={styles.sectionTitle}>{t('color') || 'Color'}</Text>
              <View style={styles.colorPicker}>
                {PRESET_COLORS.map((color) => (
                  <TouchableOpacity
                    key={color}
                    style={[
                      styles.colorOption,
                      { backgroundColor: color },
                      quickAddForm.color === color && styles.colorOptionSelected,
                    ]}
                    onPress={() => setQuickAddForm({ ...quickAddForm, color })}
                  />
                ))}
              </View>
            </ScrollView>

            <TouchableOpacity
              style={[styles.saveButton, saving && styles.buttonDisabled]}
              onPress={quickAddSpotType}
              disabled={saving}
            >
              {saving ? (
                <Spinner color="#fff" />
              ) : (
                <Text style={styles.saveButtonText}>{t('create_and_add') || 'Create & Add'}</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Spot Detail Modal */}
      <Modal visible={spotDetailModalVisible} animationType="slide" transparent>
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setSpotDetailModalVisible(false)}
        >
          <TouchableOpacity activeOpacity={1} style={styles.modalContent} onPress={(e) => e.stopPropagation?.()}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {selectedSpot?.spot_number || t('spot_details') || 'Spot Details'}
              </Text>
              <TouchableOpacity onPress={() => setSpotDetailModalVisible(false)}>
                <X size={24} color="#999" />
              </TouchableOpacity>
            </View>

            {selectedSpot && (
              <View style={styles.spotDetails}>
                <View style={styles.spotDetailRow}>
                  <Text style={styles.spotDetailLabel}>{t('type') || 'Type'}:</Text>
                  <Text style={styles.spotDetailValue}>
                    {selectedSpot.spot_type?.name || spotTypes.find(t => t.id === selectedSpot.spot_type_id)?.name}
                  </Text>
                </View>
                <View style={styles.spotDetailRow}>
                  <Text style={styles.spotDetailLabel}>{t('dimensions') || 'Dimensions'}:</Text>
                  <Text style={styles.spotDetailValue}>
                    {selectedSpot.spot_type?.width_cm || spotTypes.find(t => t.id === selectedSpot.spot_type_id)?.width_cm}cm × {selectedSpot.spot_type?.depth_cm || spotTypes.find(t => t.id === selectedSpot.spot_type_id)?.depth_cm}cm
                  </Text>
                </View>
                <View style={styles.spotDetailRow}>
                  <Text style={styles.spotDetailLabel}>{t('position') || 'Position'}:</Text>
                  <Text style={styles.spotDetailValue}>
                    {selectedSpot.x_position}cm, {selectedSpot.y_position}cm
                  </Text>
                </View>
                <View style={styles.spotDetailRow}>
                  <Text style={styles.spotDetailLabel}>{t('rotation') || 'Rotation'}:</Text>
                  <Text style={styles.spotDetailValue}>{selectedSpot.rotation}°</Text>
                </View>
                <View style={styles.spotDetailRow}>
                  <Text style={styles.spotDetailLabel}>{t('status') || 'Status'}:</Text>
                  <Text style={[styles.spotDetailValue, { color: selectedSpot.is_available ? '#4CAF50' : '#F44336' }]}>
                    {selectedSpot.is_available ? (t('available') || 'Available') : (t('reserved') || 'Reserved')}
                  </Text>
                </View>

                <View style={styles.spotActions}>
                  <TouchableOpacity
                    style={styles.spotActionButton}
                    onPress={() => { copySpot(selectedSpot || undefined); setSpotDetailModalVisible(false) }}
                  >
                    <Clipboard size={20} color="#666" />
                    <Text style={styles.spotActionText}>{t('copy') || 'Copy'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.spotActionButton}
                    onPress={() => rotateSpot(selectedSpot.id)}
                  >
                    <RotateCw size={20} color="#666" />
                    <Text style={styles.spotActionText}>{t('rotate') || 'Rotate'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.spotActionButton, styles.spotDeleteButton]}
                    onPress={() => deleteSpot(selectedSpot.id)}
                  >
                    <Trash2 size={20} color="#F44336" />
                    <Text style={[styles.spotActionText, { color: '#F44336' }]}>{t('delete') || 'Delete'}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Element Detail Modal (for walls and amenities) */}
      <Modal visible={elementDetailModalVisible} animationType="slide" transparent>
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setElementDetailModalVisible(false)}
        >
          <TouchableOpacity activeOpacity={1} style={styles.modalContent} onPress={(e) => e.stopPropagation?.()}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {selectedElement ? (
                  (() => {
                    const amenity = AMENITY_PRESETS.find(a => a.type === selectedElement.element_type)
                    return amenity ? (t(amenity.labelKey) || amenity.labelKey) :
                      (selectedElement.element_type === 'wall' ? (t('wall') || 'Wall') : selectedElement.element_type)
                  })()
                ) : (t('element_details') || 'Element Details')}
              </Text>
              <TouchableOpacity onPress={() => setElementDetailModalVisible(false)}>
                <X size={24} color="#999" />
              </TouchableOpacity>
            </View>

            {selectedElement && (
              <View style={styles.spotDetails}>
                <View style={styles.spotDetailRow}>
                  <Text style={styles.spotDetailLabel}>{t('type') || 'Type'}:</Text>
                  <Text style={styles.spotDetailValue}>
                    {(() => {
                      const amenity = AMENITY_PRESETS.find(a => a.type === selectedElement.element_type)
                      if (amenity) return `${amenity.icon} ${t(amenity.labelKey) || amenity.labelKey}`
                      if (selectedElement.element_type === 'wall') return `📏 ${t('wall') || 'Wall'}`
                      return selectedElement.element_type
                    })()}
                  </Text>
                </View>

                {selectedElement.element_type === 'wall' ? (
                  <>
                    <View style={styles.spotDetailRow}>
                      <Text style={styles.spotDetailLabel}>{t('start') || 'Start'}:</Text>
                      <Text style={styles.spotDetailValue}>
                        {selectedElement.x1}cm, {selectedElement.y1}cm
                      </Text>
                    </View>
                    <View style={styles.spotDetailRow}>
                      <Text style={styles.spotDetailLabel}>{t('end') || 'End'}:</Text>
                      <Text style={styles.spotDetailValue}>
                        {selectedElement.x2}cm, {selectedElement.y2}cm
                      </Text>
                    </View>
                    <View style={styles.spotDetailRow}>
                      <Text style={styles.spotDetailLabel}>{t('thickness') || 'Thickness'}:</Text>
                      <Text style={styles.spotDetailValue}>{selectedElement.wall_thickness}cm</Text>
                    </View>
                    <View style={styles.spotDetailRow}>
                      <Text style={styles.spotDetailLabel}>{t('length') || 'Length'}:</Text>
                      <Text style={styles.spotDetailValue}>
                        {Math.round(Math.sqrt(
                          Math.pow((selectedElement.x2 || 0) - (selectedElement.x1 || 0), 2) +
                          Math.pow((selectedElement.y2 || 0) - (selectedElement.y1 || 0), 2)
                        ))}cm
                      </Text>
                    </View>
                  </>
                ) : (
                  <>
                    <View style={styles.spotDetailRow}>
                      <Text style={styles.spotDetailLabel}>{t('position') || 'Position'}:</Text>
                      <Text style={styles.spotDetailValue}>
                        {selectedElement.x_position}cm, {selectedElement.y_position}cm
                      </Text>
                    </View>
                    <View style={styles.spotDetailRow}>
                      <Text style={styles.spotDetailLabel}>{t('dimensions') || 'Dimensions'}:</Text>
                      <Text style={styles.spotDetailValue}>
                        {selectedElement.width_cm}cm × {selectedElement.height_cm}cm
                      </Text>
                    </View>
                    <View style={styles.spotDetailRow}>
                      <Text style={styles.spotDetailLabel}>{t('rotation') || 'Rotation'}:</Text>
                      <Text style={styles.spotDetailValue}>{selectedElement.rotation}°</Text>
                    </View>
                  </>
                )}

                <View style={styles.spotActions}>
                  {selectedElement.element_type !== 'wall' && (
                    <TouchableOpacity
                      style={styles.spotActionButton}
                      onPress={() => rotateElement(selectedElement.id)}
                    >
                      <RotateCw size={20} color="#666" />
                      <Text style={styles.spotActionText}>{t('rotate') || 'Rotate'}</Text>
                    </TouchableOpacity>
                  )}
                  <TouchableOpacity
                    style={[styles.spotActionButton, styles.spotDeleteButton]}
                    onPress={() => deleteFloorElement(selectedElement.id)}
                  >
                    <Trash2 size={20} color="#F44336" />
                    <Text style={[styles.spotActionText, { color: '#F44336' }]}>{t('delete') || 'Delete'}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
</View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.primary },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.md,
    paddingTop: theme.spacing.md,
    paddingBottom: theme.spacing.sm,
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center' },
  backButtonText: {
    fontSize: 24,
    color: merchantColors.primary },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text.primary,
    marginRight: theme.spacing.md },
  headerFloorTabs: {
    flex: 1 },
  floorTabs: {
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  floorTabsContent: {
    flexDirection: 'row',
    paddingVertical: 4,
    gap: 8 },
  floorTab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.secondary },
  floorTabActive: {
    backgroundColor: merchantColors.primary },
  floorTabText: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    fontWeight: '500' },
  floorTabTextActive: {
    color: '#fff' },
  floorEditButton: {
    marginLeft: 6,
    padding: 2 },
  floorEditButtonText: {
    fontSize: 12,
    color: '#fff',
    opacity: 0.9 },
  addFloorTab: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.secondary,
    borderWidth: 1,
    borderColor: theme.colors.ui.borderLight,
    borderStyle: 'dashed' },
  addFloorTabText: {
    fontSize: 18,
    color: theme.colors.text.secondary },
  toolbar: {
    backgroundColor: theme.colors.background.card,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  toolGroup: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8 },
  toolButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.secondary,
    gap: 6 },
  toolButtonActive: {
    backgroundColor: merchantColors.primary },
  toolButtonText: {
    fontSize: 16 },
  toolButtonLabel: {
    fontSize: 14,
    color: theme.colors.text.primary,
    fontWeight: '500' },
  toolButtonLabelActive: {
    color: '#fff' },
  spotTypeSelectorContainer: {
    marginTop: 4 },
  spotTypeSelector: {
    flexDirection: 'row' },
  spotTypeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: theme.borderRadius.full,
    backgroundColor: theme.colors.background.secondary,
    borderWidth: 2,
    marginRight: 8,
    gap: 6 },
  spotTypeColorDot: {
    width: 12,
    height: 12,
    borderRadius: 6 },
  spotTypeChipText: {
    fontSize: 12,
    color: theme.colors.text.primary,
    fontWeight: '500' },
  spotTypeChipTextActive: {
    color: '#fff' },
  addSpotTypeChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: theme.borderRadius.full,
    backgroundColor: merchantColors.primary },
  addSpotTypeChipText: {
    fontSize: 12,
    color: '#fff',
    fontWeight: '600' },
  copyIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    padding: 8,
    backgroundColor: '#E8F5E9',
    borderRadius: theme.borderRadius.md,
    gap: 8 },
  copyIndicatorText: {
    flex: 1,
    fontSize: 13,
    color: '#2E7D32' },
  cancelCopy: {
    fontSize: 16,
    color: '#666',
    padding: 4 },
  numberingOptions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
    flexWrap: 'wrap' },
  numberingLabel: {
    fontSize: 13,
    color: theme.colors.text.secondary },
  numberingInput: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.sm,
    paddingHorizontal: 10,
    paddingVertical: 6,
    width: 50,
    fontSize: 14,
    color: theme.colors.text.primary,
    textAlign: 'center' },
  numberingPreview: {
    fontSize: 13,
    color: merchantColors.primary,
    fontWeight: '600',
    marginLeft: 8 },
  canvasWrapper: {
    flex: 1,
    backgroundColor: '#e8e8e8' },
  canvasContainer: {
    flex: 1,
    position: 'relative' },
  rulerContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
    zIndex: 10 },
  rulerMark: {
    position: 'absolute',
    top: 10 },
  rulerMarkV: {
    position: 'absolute',
    left: 5 },
  rulerText: {
    fontSize: 10,
    color: '#666' },
  canvasScroll: {
    flex: 1,
    marginTop: 30,
    marginLeft: 30 },
  canvasScrollContent: {
    padding: 20 },
  canvas: {
    backgroundColor: '#fff',
    borderRadius: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4 },
  emptyCanvas: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40 },
  emptyCanvasText: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 8,
    textAlign: 'center' },
  emptyCanvasHint: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    marginBottom: 24,
    textAlign: 'center' },
  addFloorButton: {
    backgroundColor: merchantColors.primary,
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: theme.borderRadius.md },
  addFloorButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600' },
  zoomControls: {
    position: 'absolute',
    right: 16,
    bottom: 16,
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 4,
    overflow: 'hidden' },
  zoomButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  zoomButtonText: {
    fontSize: 24,
    color: theme.colors.text.primary,
    fontWeight: '300' },
  zoomButtonTextSmall: {
    fontSize: 11,
    color: theme.colors.text.secondary,
    fontWeight: '600' },
  canvasInfo: {
    position: 'absolute',
    left: 16,
    bottom: 16,
    backgroundColor: 'rgba(255,255,255,0.9)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: theme.borderRadius.sm },
  dimensionLabel: {
    fontSize: 12,
    color: theme.colors.text.secondary },
  statsBar: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.md,
    backgroundColor: theme.colors.background.card,
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.borderLight },
  statText: {
    fontSize: 13,
    color: theme.colors.text.secondary },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end' },
  modalContent: {
    backgroundColor: theme.colors.background.card,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '85%' },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  modalClose: {
    fontSize: 24,
    color: theme.colors.text.secondary,
    padding: 4 },
  modalForm: {
    padding: theme.spacing.md,
    maxHeight: 450 },
  label: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    marginBottom: 4 },
  input: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.md,
    padding: 12,
    fontSize: 16,
    marginBottom: 12,
    color: theme.colors.text.primary },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginTop: 8,
    marginBottom: 12 },
  row: {
    flexDirection: 'row',
    gap: 12 },
  col: {
    flex: 1 },
  hint: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: -8,
    marginBottom: 12 },
  presetRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16 },
  presetButton: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.secondary,
    borderWidth: 1,
    borderColor: theme.colors.ui.borderLight },
  presetButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.primary },
  presetButtonSize: {
    fontSize: 11,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  colorPicker: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16 },
  colorOption: {
    width: 36,
    height: 36,
    borderRadius: 18 },
  colorOptionSelected: {
    borderWidth: 3,
    borderColor: '#000' },
  modalActions: {
    flexDirection: 'row',
    padding: theme.spacing.md,
    gap: 12 },
  deleteFloorButton: {
    flex: 1,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#F44336',
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center' },
  deleteFloorButtonText: {
    color: '#F44336',
    fontSize: 14,
    fontWeight: '600' },
  saveButton: {
    backgroundColor: merchantColors.primary,
    margin: theme.spacing.md,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center' },
  saveButtonFlex: {
    flex: 1,
    margin: 0 },
  buttonDisabled: {
    opacity: 0.6 },
  saveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600' },
  // Spot detail styles
  spotDetails: {
    padding: theme.spacing.md },
  spotDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  spotDetailLabel: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  spotDetailValue: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.primary },
  spotActions: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.borderLight },
  spotActionButton: {
    alignItems: 'center',
    padding: 12 },
  spotDeleteButton: {
    // Additional styles for delete button
  },
  spotActionIcon: {
    fontSize: 24,
    marginBottom: 4 },
  spotActionText: {
    fontSize: 14,
    color: theme.colors.text.primary },
  // Selection info styles
  selectionInfo: {
    position: 'absolute',
    left: 16,
    top: 16,
    backgroundColor: 'rgba(0, 191, 255, 0.9)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: theme.borderRadius.sm,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8 },
  selectionInfoText: {
    fontSize: 12,
    color: '#fff',
    fontWeight: '600' },
  clearSelectionButton: {
    padding: 2 },
  clearSelectionText: {
    fontSize: 14,
    color: '#fff' },
  copyButton: {
    padding: 2 },
  copyButtonText: {
    fontSize: 14 },
  copiedInfo: {
    top: 50,
    backgroundColor: 'rgba(76, 175, 80, 0.9)' },
  // Wall and amenity styles
  toolDivider: {
    width: 1,
    height: 32,
    backgroundColor: theme.colors.ui.borderLight || '#e0e0e0',
    marginHorizontal: 4 },
  wallOptions: {
    marginTop: 8,
    padding: 12,
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.sm },
  wallInstructions: {
    fontSize: 13,
    color: theme.colors.text.secondary,
    marginBottom: 8,
    fontStyle: 'italic' },
  wallControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap' },
  wallColorPicker: {
    flexDirection: 'row',
    gap: 6 },
  wallColorOption: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: 'transparent' },
  wallColorOptionSelected: {
    borderColor: '#00BFFF' },
  cancelWallButton: {
    marginTop: 8,
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: theme.colors.background.secondary || '#f0f0f0',
    borderRadius: theme.borderRadius.sm,
    alignSelf: 'flex-start' },
  cancelWallText: {
    color: theme.colors.text.secondary,
    fontSize: 12 },
  amenitySelector: {
    marginTop: 8 },
  amenityChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 2,
    marginRight: 8,
    backgroundColor: theme.colors.background.primary },
  amenityIcon: {
    fontSize: 18,
    marginRight: 6 },
  amenityLabel: {
    fontSize: 12,
    color: theme.colors.text.primary,
    fontWeight: '500' },
  amenityLabelSelected: {
    color: '#fff' },
  amenityHint: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 8,
    fontStyle: 'italic' } })
